import axios from "axios";
import { defineStore } from "pinia";
import { useWorkspace } from "./Workspaces";
import { useWorkspaceTree } from "./WorkspaceTree";
import { useUserStore } from "./User";
import Utilities from "../Helpers/utilities";
import isEmpty from "lodash/isEmpty";
import cloneDeep from "lodash/cloneDeep";
import { useConversation } from "./Conversation";
import { useAppState } from "./AppState";
import { useAppState as useScreenshotAppState } from "../Components/ScreenShots/Store/AppState";
import { useTimeTrackerStore } from "./TimeTracker";
import utilities from "../Helpers/utilities";
import DateUtilities from "../Utilities/DateUtilities";
import { useDexieCrud } from "js/Admin/js/Composables/useDexieCrud";
import { TABLES } from "js/Admin/js/Utilities/dexieConnections";
import { getImageUrlToBlob } from "js/Admin/js/Helpers/common";
import {
    collectReportsToDownlineProfileIds,
    isOnReportsToDownline,
} from "js/Admin/js/Helpers/reportsToDownline";
import {
    collectAccessibleTeamPanelProfileIds,
    isStaleScreenshotProfileResponse,
    shouldResetEveryoneTeamPanelSelection,
} from "js/Admin/js/Helpers/everyoneTeamPanelScope";
import { shouldSkipTeamPanelRootFetch } from "js/Admin/js/Helpers/teamPanelRootInitialization";

const DEFAULT_GROUP = "Backlog";

/**
 * @param {number|string|null} itemId
 * @returns {{ item: Object|null, board: Object|null, space: Object|null, item_category: Object|null }}
 */
function getSpaceBoardForItemFromTree(itemId) {
    const tree = useWorkspaceTree();
    const item = tree.getItemById(itemId);
    if (!item) {
        return {
            item: null,
            board: null,
            space: null,
            item_category: null,
        };
    }
    const boardId = item.boardId ?? item.board_id;
    const workspaceId = item.workspaceId ?? item.workspace_id;
    const itemCategoryId = item.itemCategoryId ?? item.item_category_id;

    return {
        item,
        board: boardId ? tree.getBoardById(boardId) : null,
        space: workspaceId ? tree.getWorkspaceById(workspaceId) : null,
        item_category: itemCategoryId
            ? tree.getItemCategoryById(itemCategoryId)
            : null,
    };
}

const SCREENSHOT_DISABLE = 0;
const SCREENSHOT_OWNER_USER = 2;
const SCREENSHOT_OWNER_MANAGER_USER = 3;

export const TEAM_WORK_TABS = Object.freeze({
    BACKLOG: "backlog",
    SCREENSHOTS: "screenshots",
    WORK_JOURNAL: "work-journal",
    USER_STORIES: "stories",
});

export const useTeamPanel = defineStore("TeamPanelStore", {
    state: () => ({
        team_panel_title: DEFAULT_GROUP,
        user_task_groups: [],
        team_panel_data_map: new Map(),
        selected_member: {},
        opened_member: {},
        selected_subordinate: {},
        is_dragged_target_category: null,
        team_panel_data: [],
        team_panel_data_prepared: false,
        team_panel_subordinates: false,
        pinned_team_member: null,
        new_current_user: null,
        screenshots: [],
        subordinates: [],
        default_group: DEFAULT_GROUP,
        has_team_active_item: false,
        /**
         * Workspace item id selected in the team backlog / kanban (cards).
         * Separate from workspace_tree.activeItem hydration: tree rows may be absent
         * while the member task list still has full `item` payloads.
         */
        team_kanban_active_item_id: null,
        team_filter: [
            { key: "shift-in", text: "Shifted In", checked: true },
            {
                key: "shift-out",
                text: "Shifted Out",
                checked: true,
            },
        ],
        preserve_shift_filter: false,
        active_tab: TEAM_WORK_TABS.BACKLOG,
        filtered_people_count: 0,
        is_scrolling_people: false,
        is_scrolling_catgories: false,
        sub_level_list: [],
        team_count: 0,
        team_hierarchy: [],
        documentDetails: {
            open: false,
            conv_id: undefined,
            is_open_from_hover_menu: false,
            is_activity_drawer_full_width: false,
        },
        forbidden_profile_ids: [],

        screenshotsData: {
            LIMIT: 200,
            data: [],
            loading: false,
            loadingMore: false,
            hasMore: false,
            fetchId: 0,
        },
        last_team_member_locate: {
            assigner_id: null,
            assignee_id: null,
        },
        navigate_to_user: false,
        userStories: new Map(),
        isLoadingUserStories: false,
        flatUserTaskList: [],
        isReportingHierarchyRefreshing: false,
    }),
    getters: {
        user_task_groups_new() {
            const contextId = this.getCurrentUserProfileId();
            return this.team_panel_data_map.get(contextId) ?? {};
        },
        profiles_with_tasks() {
            const root = this.user_task_groups_new;
            if (!root || !root.user_profile_id) return [];

            const rootId = root.user_profile_id ?? root.id;
            const rootProfile = this.team_panel_data.find(
                (profile) => profile.user_profile_id === rootId
            );

            const subordinateProfiles = (root.subordinates ?? []).map(sub => {
                const subId = sub.user_profile_id ?? sub.id;
                const profile = this.team_panel_data.find(
                    (item) => item.user_profile_id === subId
                );
                if (!profile) {
                    return null;
                }

                return {
                    ...profile,
                    relation: sub.relation ?? profile.relation,
                };
            }).filter(Boolean);

            const result = [];
            if (rootProfile) {
                result.push(rootProfile);
            }
            result.push(...subordinateProfiles);
            return result;
        },
        user_default_group: (state) => state.default_group,
        is_prepared: (state) => state.team_panel_data_prepared,
        is_activity_drawer_team_notes_full_view: (state) => {
            const { open, is_activity_drawer_full_width } =
                state.documentDetails;
            return Boolean(open && is_activity_drawer_full_width);
        },
        can_access_screenshot_tab: (state) => {
            const screenshot_visibility_level =
                useUserStore().getCurrentUserProfile().organization
                    .screenshot_visibility_level;
            const selected_user_profile_id =
                state.selected_member?.user_profile_id;
            const current_profile_id = useUserStore().profile?.id;

            if (screenshot_visibility_level === SCREENSHOT_DISABLE) {
                return false;
            }

            const user_store = useUserStore();

            if (
                selected_user_profile_id &&
                user_store.isOrganizationOwnerProfile(
                    selected_user_profile_id,
                ) &&
                selected_user_profile_id !== current_profile_id
            ) {
                return false;
            }

            if (
                user_store.isAuthorizedFor("owner") ||
                user_store.hasPermissionForScreenshots(
                    selected_user_profile_id,
                ) ||
                user_store.isAuthorizedFor("can-view-everyone-screenshots")
            ) {
                return true;
            }

            if (
                useUserStore().isAuthorizedFor("can-view-downline-screenshots") &&
                isOnReportsToDownline(
                    state.subordinates,
                    current_profile_id,
                    selected_user_profile_id,
                )
            ) {
                return true;
            }
            if (screenshot_visibility_level === SCREENSHOT_OWNER_USER) {
                return selected_user_profile_id === current_profile_id;
            }

            if (screenshot_visibility_level === SCREENSHOT_OWNER_MANAGER_USER) {
                return (
                    selected_user_profile_id === current_profile_id ||
                    state.subordinates
                        ?.find(
                            (subordinate) =>
                                subordinate.assigner_id === current_profile_id,
                        )
                        ?.assignees?.includes(selected_user_profile_id)
                );
            }

            return false;
        },

        can_delete_screenshot_tab: (state) => {
            const user_store = useUserStore();
            const current_profile_id = user_store.profile?.id;
            const selected_user_profile_id =
                state.selected_subordinate?.user_profile_id ??
                state.selected_member?.user_profile_id;

            // Org owner / org admin can always delete.
            if (user_store.hasAdministrativePermission()) {
                return true;
            }

            // Everyone else — including profiles with can-view-everyone-screenshots
            // or can-view-downline-screenshots — can only delete their own
            // screenshots, i.e. only when the profile being viewed is themselves.
            return (
                selected_user_profile_id != null &&
                selected_user_profile_id === current_profile_id
            );
        },
        /**
         * Full `item` object for the team kanban selection, from the current
         * member's task rows (when the workspace tree does not have the item yet).
         */
        teamKanbanResolvedItem(state) {
            const id = state.team_kanban_active_item_id;
            if (id == null || id === "") {
                return null;
            }
            const groups = state.selected_member?.tasks?.data ?? [];
            for (const group of groups) {
                for (const row of group.items ?? []) {
                    const itemId = row?.item?.id ?? row?.item_id;
                    if (itemId != null && itemId == id) {
                        return row.item ?? null;
                    }
                }
            }

            return null;
        },
    },
    actions: {
        markProfileAsForbidden(profileId) {
            const id = Number(profileId);
            if (id && !this.forbidden_profile_ids.includes(id)) {
                this.forbidden_profile_ids.push(id);
            }
        },
        clearForbiddenProfile(profileId) {
            const id = Number(profileId);
            this.forbidden_profile_ids = this.forbidden_profile_ids.filter((pId) => pId !== id);
        },
        isProfileForbidden(profileId) {
            const id = Number(profileId);
            return this.forbidden_profile_ids.includes(id);
        },
        isActiveTab(tab) {
            return this.active_tab === tab;
        },
        setActiveTab(tab) {
            this.active_tab = tab;
            if (tab !== TEAM_WORK_TABS.BACKLOG) {
                this.team_kanban_active_item_id = null;
            }
        },
        toggleDocument(
            show = false,
            convId = undefined,
            is_open_from_hover_menu = false,
            is_activity_drawer_full_width = false,
        ) {
            this.documentDetails = {
                open: show,
                conv_id: show ? convId : undefined,
                is_open_from_hover_menu: is_open_from_hover_menu && show,
                is_activity_drawer_full_width:
                    show && is_activity_drawer_full_width,
            };
            useAppState().updateDocumentState(show);
        },
        async reInitializeTeamPanel() {
            this.setTeamPanelDatePrepared(false);
            await this.initializeTeamPanel();
        },
        prefetchSubordinates(responseData) {
            if (!responseData || !Array.isArray(responseData.subordinates)) {
                return;
            }
            console.log("[TeamPanelStore] prefetchSubordinates start: prefetching level-1 subordinates of", responseData.user_profile_id ?? responseData.id);
            responseData.subordinates.forEach((sub) => {
                const subId = sub.user_profile_id ?? sub.id;
                if (subId != null && !this.team_panel_data_map.has(subId) && sub.subordinates.length > 0) {
                    console.log("[TeamPanelStore] prefetchSubordinates calling API in background for:", subId, sub.full_name);
                    axios.get(`/team-panel/${subId}/${subId}`)
                        .then((response) => {
                            this.clearForbiddenProfile(subId);
                            const respData = response.data?.data ?? {};
                            this.team_panel_data_map.set(subId, respData);
                            this.team_panel_data_map = new Map(this.team_panel_data_map);
                            console.log("[TeamPanelStore] prefetchSubordinates success for:", subId);
                        })
                        .catch((error) => {
                            if (error.response?.status === 403) {
                                this.markProfileAsForbidden(subId);
                            }
                            console.error("[TeamPanelStore] prefetchSubordinates failed for:", subId, error);
                        });
                }
            });
        },
        syncTeamPanelDataForContext() {
            const contextId = this.getCurrentUserProfileId();
            const data = this.team_panel_data_map.get(contextId);
            console.log("[TeamPanelStore] syncTeamPanelDataForContext start", { contextId, has_data: !isEmpty(data) });
            if (!isEmpty(data)) {
                this.team_panel_data = [];
                this.addLoginProfileTeamPanelData(data);
                if (this.getAllProfiles().length > 0) {
                    this.generateTeamPanelData(data?.subordinates);
                }
                this.reorderTeamPanel();
                this.prefetchSubordinates(data);
            }
        },
        async initializeTeamPanel() {
            const viewerProfileId = useUserStore().profile.id;

            if (
                shouldSkipTeamPanelRootFetch({
                    teamPanelDataPrepared: this.team_panel_data_prepared,
                    teamPanelDataMap: this.team_panel_data_map,
                    viewerProfileId,
                })
            ) {
                return;
            }

            await this.fetchSubordinates();

            const targetId = viewerProfileId;
            const contextId = targetId;

            await axios
                .get(`/team-panel/${targetId}/${contextId}`)
                .then((data) => {
                    this.clearForbiddenProfile(contextId);
                    const responseData = data.data?.data ?? {};
                    this.team_panel_data_map.set(contextId, responseData);
                    this.prefetchSubordinates(responseData);
                    this.team_panel_data_map = new Map(this.team_panel_data_map);
                    this.prepareFlatUserTaskList();
                    this.prepareTeamPanel(responseData);
                    this.initializeTeamFilter();
                    return data;
                })
                .catch((error) => {
                    if (error.response?.status === 403) {
                        this.markProfileAsForbidden(contextId);
                    }
                    throw error;
                });

            const activeItem = this.selected_member?.active_task;
            if (activeItem) {
                this.setTeamKanbanActiveItemId(activeItem.id);
            }
        },
        prepareFlatUserTaskList() {
            const result = [];
            const seen = new Set();
            let orderCounter = 0;

            const processUser = (user) => {
                if (!user) return;

                if (!seen.has(user.user_profile_id)) {
                    seen.add(user.user_profile_id);

                    result.push({
                        id: user.user_profile_id,
                        full_name: user.full_name,
                        email: user.email,
                        avatar_url: user.avatar_url,
                        user_profile_id: user.user_profile_id,
                        is_force_app_shift: user.is_force_app_shift ?? false,
                        is_active: user.is_active ?? 1,
                        root_order: orderCounter,
                        data: user.groups ?? [],
                        order: orderCounter++,
                    });
                }

                // Always recurse even if duplicate — may have deeper subordinates
                if (
                    Array.isArray(user.subordinates) &&
                    user.subordinates.length > 0
                ) {
                    user.subordinates.forEach((sub) => processUser(sub));
                }
            };

            const root =
                this.user_task_groups_new?.data ?? this.user_task_groups_new;
            processUser(root);

            this.flatUserTaskList = result;
        },
        getCurrentUserProfileId() {
            if (this.team_panel_subordinates) return this.new_current_user;

            return useUserStore().profile.id;
        },
        getCurrentProfile() {
            return useUserStore().profile;
        },
        getAllProfiles() {
            return useUserStore().profiles;
        },
        getFilterProfile() {
            return isEmpty(this.selected_subordinate)
                ? this.selected_member
                : this.selected_subordinate;
        },
        getFilterAssigner() {
            return isEmpty(this.selected_subordinate)
                ? this.byUserProfileTask(this.getCurrentUserProfileId())
                : this.selected_member;
        },
        getAssignersByProfile(assignee_profile_id) {
            const assigners = new Set();
            this.team_panel_data
                .find(
                    (profile) =>
                        profile.user_profile_id === assignee_profile_id,
                )
                ?.data?.forEach((group) =>
                group?.items.forEach((task_item) => {
                    if (task_item?.item?.version?.user_profile_id_creator)
                        assigners.add(
                            task_item?.item?.version
                                ?.user_profile_id_creator,
                        );
                }),
            );
            return assigners;
        },
        async fetchSubordinates() {
            return axios
                .get("/user-profile-tasks/subordinates")
                .then(({ data }) => {
                    this.subordinates = data.data;
                    return data.data;
                });
        },
        /**
         * Rebuilds team-panel membership from `reports_to_profile_id` after worker settings
         * (or LiveSync profile updates) change the reporting hierarchy.
         */
        async refreshReportingHierarchy() {
            if (!this.team_panel_data_prepared) {
                return;
            }

            if (this.isReportingHierarchyRefreshing) {
                return;
            }

            try {
                this.isReportingHierarchyRefreshing = true;
                await this._refreshReportingHierarchy();
            } catch (error) {
                console.error("refreshReportingHierarchy failed", error);
            } finally {
                this.isReportingHierarchyRefreshing = false;
            }
        },
        async _refreshReportingHierarchy() {
            await this.fetchSubordinates();

            const targetId = this.getCurrentUserProfileId();
            const contextId = useUserStore().profile.id;

            let axiosResponse;
            try {
                axiosResponse = await axios.get(
                    `/team-panel/${targetId}/${contextId}`,
                );
                this.clearForbiddenProfile(targetId);
            } catch (error) {
                if (error.response?.status === 403) {
                    this.markProfileAsForbidden(targetId);
                }
                throw error;
            }
            // Match `initializeTeamPanel`: API body is `{ data: teamRoot, ... }`.
            const task_data = axiosResponse.data?.data ?? {};
            const rootProfileId =
                task_data?.user_profile_id ?? task_data?.id ?? null;

            if (rootProfileId == null) {
                this.reorderTeamPanel();
                useAppState().requestTeamDashboardRefresh();
                return;
            }

            this.team_panel_data = [];
            this.team_hierarchy = [];
            this.team_panel_data_map.set(targetId, task_data);
            this.prefetchSubordinates(task_data);
            this.team_panel_data_map = new Map(this.team_panel_data_map);
            this.prepareFlatUserTaskList();
            this.prepareTeamPanel(task_data);
            this.reorderTeamPanel();

            useAppState().requestTeamDashboardRefresh();
        },
        /**
         * After everyone-mandate / everyone-screenshots flips: refetch the viewer's
         * own root panel (the only payload that includes the HR batch), replace the
         * cached root list, and drop a selected or drilled-into member who is no
         * longer on that panel.
         */
        async refreshEveryoneTeamPanelScope() {
            if (!this.team_panel_data_prepared) {
                return;
            }

            if (this.isReportingHierarchyRefreshing) {
                return;
            }

            try {
                this.isReportingHierarchyRefreshing = true;
                this.screenshotsData.fetchId += 1;
                await this._refreshEveryoneTeamPanelScope();
            } catch (error) {
                console.error("refreshEveryoneTeamPanelScope failed", error);
            } finally {
                this.isReportingHierarchyRefreshing = false;
            }
        },
        async _refreshEveryoneTeamPanelScope() {
            await this.fetchSubordinates();

            const viewerId = useUserStore().profile.id;
            const previousSelectedId =
                this.selected_member?.user_profile_id ??
                this.selected_member?.id;
            const previousDrilledId = this.team_panel_subordinates
                ? (this.new_current_user ??
                    this.opened_member?.user_profile_id ??
                    this.opened_member?.id)
                : null;

            let axiosResponse;
            try {
                axiosResponse = await axios.get(
                    `/team-panel/${viewerId}/${viewerId}`,
                );
                this.clearForbiddenProfile(viewerId);
            } catch (error) {
                if (error.response?.status === 403) {
                    this.markProfileAsForbidden(viewerId);
                }
                throw error;
            }

            const task_data = axiosResponse.data?.data ?? {};
            const rootProfileId =
                task_data?.user_profile_id ?? task_data?.id ?? null;

            if (rootProfileId == null) {
                this.resetEveryoneTeamPanelDrillToViewer();
                this.selectActivePeopleForUserProfileId(viewerId);
                useAppState().requestTeamDashboardRefresh();
                return;
            }

            const accessibleIds =
                collectAccessibleTeamPanelProfileIds(task_data);
            const mustDropSelection = shouldResetEveryoneTeamPanelSelection({
                viewerId,
                selectedId: previousSelectedId,
                drilledId: previousDrilledId,
                accessibleIds,
            });

            if (mustDropSelection) {
                this.clearScreenshotsData();
            }

            this.resetEveryoneTeamPanelDrillToViewer();
            this.team_panel_data = [];
            this.team_hierarchy = [];
            this.team_panel_data_map.set(viewerId, task_data);
            this.prefetchSubordinates(task_data);
            this.team_panel_data_map = new Map(this.team_panel_data_map);
            this.prepareFlatUserTaskList();
            this.addLoginProfileTeamPanelData(task_data);
            if (this.getAllProfiles().length > 0) {
                this.generateTeamPanelData(task_data?.subordinates);
            }
            this.reorderTeamPanel();

            const keepSelectedId =
                !mustDropSelection &&
                previousSelectedId != null &&
                this.isOnProfilesWithTasksList(previousSelectedId)
                    ? previousSelectedId
                    : viewerId;

            this.selectActivePeopleForUserProfileId(keepSelectedId);

            const pinnedId =
                this.pinned_team_member?.user_profile_id ??
                this.pinned_team_member?.id;
            if (
                pinnedId != null &&
                !accessibleIds.has(Number(pinnedId))
            ) {
                this.pinned_team_member = null;
            }

            useAppState().requestTeamDashboardRefresh();
        },
        resetEveryoneTeamPanelDrillToViewer() {
            this.team_panel_subordinates = false;
            this.new_current_user = null;
            this.sub_level_list = [];
            this.opened_member = {};
            this.team_count = 0;
            this.selected_subordinate = {};
        },
        removeMembersByUserProfileIds(profile_ids) {
            const ids = [
                ...new Set(
                    profile_ids
                        ?.filter((id) => id != null && id !== "")
                        ?.map((id) => Number(id)) ?? [],
                ),
            ];
            if (!ids.length || !this.team_panel_data_prepared) {
                return;
            }

            this.team_panel_data = this.team_panel_data.filter(
                (row) => !ids.includes(Number(row.user_profile_id)),
            );

            const selected_id = this.selected_member?.user_profile_id;
            if (selected_id != null && ids.includes(Number(selected_id))) {
                this.onEmptySelectedMemberList(this.getCurrentUserProfileId());
            }

            const pinned_id =
                this.pinned_team_member?.user_profile_id ??
                this.pinned_team_member?.id;
            if (pinned_id != null && ids.includes(Number(pinned_id))) {
                this.pinned_team_member = null;
            }

            const sub_id = this.selected_subordinate?.user_profile_id;
            if (sub_id != null && ids.includes(Number(sub_id))) {
                this.selected_subordinate = {};
            }

            this.filterDataOnLiveSync();
        },

        pushProfileToTeamPanel(profile_to_add) {
            const user_profile_index = this.team_panel_data.findIndex(
                (user_profile) =>
                    user_profile.user_profile_id ===
                    profile_to_add.user_profile_id,
            );
            if (user_profile_index < 0) {
                this.team_panel_data.push(profile_to_add);
                return;
            }
            this.team_panel_data.splice(user_profile_index, 1, profile_to_add);
            return;

            const current_user_subordinate = this.subordinates.find(
                (subordinate) =>
                    subordinate.assigner_id === this.getCurrentUserProfileId(),
            );
            // make sure current user has assigned any task to new profile or its own profile.
            if (
                current_user_subordinate?.assignees?.includes(
                    profileToAdd.user_profile_id,
                ) ||
                profileToAdd.user_profile_id === this.getCurrentUserProfileId()
            ) {
                this.team_panel_data.push(profileToAdd);
            }
        },
        setPinnedTeamMember(member) {
            this.pinned_team_member = member;
        },
        setSelectedTeamMemberById(profile_id) {
            const profile = this.getAllProfiles().find(
                (profile) =>
                    profile.user_profile_id === profile_id ||
                    profile.id === profile_id,
            );
            useWorkspace().toggleDocument(false);

            if (!isEmpty(profile)) {
                const selected_profile_id =
                    profile.user_profile_id ?? profile.id;
                const member_profile =
                    this.byUserProfileTask(selected_profile_id);
                this.setSelectedTeamMember(
                    !isEmpty(member_profile)
                        ? member_profile
                        : {
                            ...profile,
                            user_profile_id: selected_profile_id,
                        },
                );
            }
        },

        /**
         * Same flow as Team/People.vue selectedMember: clear screenshot selection,
         * setTeamHierarchy(..., 0), then setSelectedTeamMemberById.
         * Used when opening an associate dashboard for a user from the team dashboard avatars.
         */
        selectActivePeopleForUserProfileId(userProfileId) {
            if (userProfileId == null) {
                return;
            }

            useScreenshotAppState().setSelectedScreenshot([], true);

            const member = this.team_panel_data.find(
                (p) =>
                    p.user_profile_id === userProfileId ||
                    p.id === userProfileId,
            );

            if (!isEmpty(member)) {
                this.setTeamHierarchy(true, member, 0);
                this.setSelectedTeamMemberById(member.user_profile_id);
                return;
            }

            const profile = this.getAllProfiles().find(
                (p) =>
                    p.user_profile_id === userProfileId ||
                    p.id === userProfileId,
            );
            if (!isEmpty(profile)) {
                this.setTeamHierarchy(true, profile, 0);
            }
            this.setSelectedTeamMemberById(userProfileId);
            this.syncTeamPanelDataForContext();
        },

        /**
         * Apply one primary-list (`profiles_with_tasks`) row so backlog, team kanban,
         * screenshots, stories, and work journal all follow the same member.
         */
        applyPrimaryTeamMemberForWorkTabs(member) {
            if (!member) {
                return;
            }
            const has_row_id =
                member.user_profile_id != null || member.id != null;
            if (!has_row_id) {
                return;
            }

            useScreenshotAppState().setSelectedScreenshot([], true);
            this.userStories.clear();
            this.team_kanban_active_item_id = null;

            // Only mutate subordinate/drill state when we are actually in
            // subordinate mode. Calling toggleSubordinates(false) unconditionally
            // fires a watcher in People.vue that triggers syncSelectedMemberToFilteredList,
            // which can race and override the selection we are about to apply here.
            if (this.team_panel_subordinates) {
                this.toggleSubordinates(false);
                this.sub_level_list = [];
                this.opened_member = {};
                this.team_count = 0;
            }

            this.setTeamHierarchy(true, member, 0);
            this.setSelectedTeamMember(member);
            this.reorderTeamPanel();
        },

        /**
         * BFS through user_task_groups_new (the API tree already fetched) to
         * find the path of user_profile_ids from the root node down to targetId.
         * Returns [root_id, ...intermediates, targetId] or null if not found.
         *
         * @param {number} targetId
         * @returns {number[]|null}
         */
        findPathInTaskTree(targetId) {
            const root =
                this.user_task_groups_new?.data ?? this.user_task_groups_new;
            if (!root) return null;

            // DFS with path tracking
            const dfs = (node, path) => {
                if (!node) return null;
                const nodeId = Number(node.user_profile_id);
                const currentPath = [...path, nodeId];
                if (nodeId === targetId) return currentPath;
                for (const sub of node.subordinates ?? []) {
                    const result = dfs(sub, currentPath);
                    if (result) return result;
                }
                return null;
            };

            return dfs(root, []);
        },


        /**
         * Drill down through the subordinate tree until `targetProfileId` is
         * visible in the people panel, then select them.
         * Uses the user_task_groups_new API tree (already in store) to find
         * the path — no dependency on getSubordinatesByProfile type matching.
         *
         * @param {number|string} targetProfileId
         */
        drillDownToProfile(targetProfileId) {
            const targetId = Number(targetProfileId);
            if (!targetId) return;

            // Reset drill state unconditionally without triggering the
            // team_panel_subordinates watcher (set the raw state values directly
            // so People.vue doesn't fire syncSelectedMemberToFilteredList mid-drill).
            this.team_panel_subordinates = false;
            this.new_current_user = null;
            this.sub_level_list = [];
            this.opened_member = {};
            this.team_count = 0;
            this.team_hierarchy = [];

            // Check if target is directly visible (in profiles_with_tasks root list)
            const isDirectlyVisible = this.profiles_with_tasks.some(
                (p) => Number(p.user_profile_id ?? p.id) === targetId,
            );

            if (isDirectlyVisible) {
                const member =
                    this.byUserProfileTask(targetId) ??
                    this.getAllProfiles().find(
                        (p) => Number(p.user_profile_id ?? p.id) === targetId,
                    );
                if (member) {
                    this.setTeamHierarchy(true, member, 0);
                    this.setSelectedTeamMemberById(targetId);
                }
                return;
            }

            // Find path through the API tree
            const path = this.findPathInTaskTree(targetId);
            if (!path || path.length < 2) {
                // No path found — reset team_hierarchy to just the target so
                // any stale hierarchy from a previous navigation is cleared
                // before the routing code in ContextMenu reads it.
                const fallbackMember =
                    this.byUserProfileTask(targetId) ??
                    this.getAllProfiles().find(
                        (p) => Number(p.user_profile_id ?? p.id) === targetId,
                    );
                if (fallbackMember) {
                    this.setTeamHierarchy(true, fallbackMember, 0);
                }
                this.setSelectedTeamMemberById(targetId);
                return;
            }

            // path[0] = root (logged-in user), path[last] = target
            const rootMember =
                this.byUserProfileTask(path[0]) ??
                this.getAllProfiles().find(
                    (p) => Number(p.user_profile_id ?? p.id) === path[0],
                );
            if (rootMember) {
                this.setTeamHierarchy(true, rootMember, 0);
            }

            // Replay onDrillDown for each step after the root (skip index 0)
            for (let i = 1; i < path.length; i++) {
                const stepId = path[i];
                const stepMember = this.memberForDownlineRouteDrill(stepId);
                if (!stepMember || isEmpty(stepMember)) break;
                this.team_count += 1;
                this.setTeamHierarchy(true, stepMember);
                this.onDrillDown(stepMember, false, true);
            }
        },

        /**
         * Whether a profile id appears on the primary `profiles_with_tasks` list.
         *
         * @param {string|number|null|undefined} userProfileId
         */
        isOnProfilesWithTasksList(userProfileId) {
            if (userProfileId == null || userProfileId === "") {
                return false;
            }

            const main_list = this.profiles_with_tasks;
            if (!main_list?.length) {
                return false;
            }

            return main_list.some(
                (p) =>
                    p.user_profile_id == userProfileId || p.id == userProfileId,
            );
        },

        /**
         * Resolve the preferred profile on the primary team list, or fall back to
         * index 0. Updates selection for backlog / kanban / screenshots / stories /
         * work journal via `applyPrimaryTeamMemberForWorkTabs`.
         *
         * @param {string|number|null|undefined} preferredUserProfileId
         * @returns {Object|null} The applied primary-list row, or null if list empty.
         */
        fallbackTeamWorkToPrimaryListIndexZero(preferredUserProfileId = null) {
            const main_list = this.flatUserTaskList;
            if (!main_list?.length) {
                this.onEmptySelectedMemberList();
                return null;
            }

            const effective_id =
                preferredUserProfileId ??
                this.selected_member?.user_profile_id ??
                this.selected_subordinate?.user_profile_id;

            // Resolve the member object from flatUserTaskList or team_panel_data
            const resolved_member =
                effective_id == null || effective_id === ""
                    ? null
                    : main_list.find(
                        (p) =>
                            p.user_profile_id == effective_id ||
                            p.id == effective_id,
                    ) ??
                    this.team_panel_data.find(
                        (p) =>
                            p.user_profile_id == effective_id ||
                            p.id == effective_id,
                    ) ??
                    null;

            if (resolved_member) {
                // Always drill down so the people panel navigates to the correct
                // level and the user is visible + selected. drillDownToProfile
                // handles both direct-visibility and multi-level subordinate cases.
                this.drillDownToProfile(
                    resolved_member.user_profile_id ?? resolved_member.id,
                );
                return resolved_member;
            }

            this.applyPrimaryTeamMemberForWorkTabs(main_list[0]);
            return main_list[0];
        },

        async selectWorkingTask(profile_id) {
            const profile = this.getAllProfiles().find(
                (profile) => profile.user_profile_id === profile_id,
            );
            const item = profile?.active_task || profile?.paused_task;
            if (item) {
                const assigner_id = item.version?.user_profile_id_creator;

                if (
                    !useUserStore().isCurrentProfile(assigner_id) &&
                    this.profiles_with_tasks.findIndex(
                        (profile) => profile.user_profile_id === assigner_id,
                    ) !== -1 &&
                    !this.isSelfAssigned(assigner_id, item)
                ) {
                    const assigner = this.getAllProfiles().find(
                        (profile) => profile.user_profile_id === assigner_id,
                    );

                    this.setSelectedTeamMember(assigner, profile);
                }

                this.setTeamKanbanActiveItemId(item.id);
                if (useAppState().is_conversation_open) {
                    useConversation().openConversationForItem(item);
                }
            }

            if (!profile?.active_task)
                utilities().showInfoSnackbar(
                    `<b>${profile?.display_name || profile?.full_name}</b> has not started a task.`,
                    profile?.avatar_url,
                );
        },
        setSelectedTeamMember(member, subordinate = {}) {
            const prevMemberId = this.selected_member?.user_profile_id;
            const nextMemberId = member?.user_profile_id;
            if (prevMemberId !== nextMemberId) {
                this.team_kanban_active_item_id = null;
            }

            this.selected_member = member;
            this.selected_subordinate = subordinate;
            this.setTeamPanelTitle();
            this.setSelectedMemberTasks();
        },
        onEmptySelectedMemberList(fallback_profile_id = null) {
            // set own user as a selected.
            if (
                this.selected_member?.user_profile_id ===
                useUserStore().profile.id
            ) {
                this.team_panel_subordinates = false;
            }

            // When a fallback_profile_id is explicitly provided (e.g. after the
            // previously-selected member was removed from team_panel_data), use it
            // directly so we never re-select a member that no longer exists in the
            // panel. Otherwise preserve the previous behaviour of keeping the current
            // selection when it is still valid.
            const target_id =
                fallback_profile_id ??
                this.selected_member?.user_profile_id ??
                this.getCurrentUserProfileId();

            this.setSelectedTeamMemberById(target_id);
        },
        setTeamPanelTitle() {
            let selected_member_title = "";
            let subordinate_title = "";

            if (!isEmpty(this.selected_member))
                selected_member_title =
                    this.selected_member?.full_name ||
                    this.selected_member?.email;

            if (!isEmpty(this.selected_subordinate))
                subordinate_title +=
                    this.selected_subordinate?.full_name ||
                    this.selected_subordinate?.email;

            if (selected_member_title && subordinate_title) {
                this.team_panel_title = `${selected_member_title} > ${subordinate_title}`;
                return;
            }

            this.team_panel_title = selected_member_title;
        },

        setSelectedMemberTasks() {
            // retrieve whose data want to see
            let profile = this.getFilterProfile();

            // face only in local. if no selected member return.
            if (isEmpty(profile)) return;

            // retrieve who assigned data to profile.
            let assigner = this.getFilterAssigner();

            const current_task_profile = this.byUserProfileTask(
                profile.user_profile_id,
            );
            const task_profile = cloneDeep(current_task_profile);

            this.selected_member.tasks = task_profile;
            const team_ids = this.team_hierarchy.map((member) => member?.id);

            if (!this.team_panel_data_prepared) {
                return;
            }

            useAppState().updateTeam({
                selected_user_id: this.selected_member?.id,
                assigner_id: assigner?.user_profile_id,
                is_subordinate_panel: this.team_panel_subordinates,
                selected_subordinate_id:
                    this.selected_subordinate?.user_profile_id || false,
                team_hierarchy: team_ids ?? [],
            });
        },
        byUserProfileTask(user_profile_id) {
            return this.team_panel_data.find(
                (task) => task.user_profile_id == user_profile_id,
            );
        },
        findItem(item_id) {
            return this.selected_member.tasks.data.find((v) =>
                v.item_ids.includes(item_id),
            );
        },
        findItemProfileTasks(item_id) {
            return this.team_panel_data.find((task_group) => {
                return task_group.data.find((task) => {
                    return task?.item_ids?.includes(item_id);
                });
            });
        },
        findItemByUserProfileTasks(item_id, user_profile_id) {
            return this.team_panel_data.find((task_group) => {
                return task_group.data.find((task) => {
                    return (
                        task.user_profile_id == user_profile_id &&
                        task?.item_ids?.includes(item_id)
                    );
                });
            });
        },
        byUserProfileAssignedTaskGroup(user_profile_id) {
            return this.team_panel_data
                .find(
                    (task_group) =>
                        task_group.user_profile_id == user_profile_id,
                )
                ?.data?.find((task) => task.name == "Assigned");
        },
        removeItemFromTaskGroup(item_id) {
            const findItemTaskGroups = this.findItemProfileTasks(item_id);

            if (findItemTaskGroups?.data.length == 0) return;

            findItemTaskGroups?.data.forEach((assignedGroup, index) => {
                this.removeItemFromUserTaskItemGroup(assignedGroup, item_id);
            });

            if (
                findItemTaskGroups?.data?.filter(
                    (task_group) =>
                        task_group?.items.filter((task) => task).length,
                ).length == 0 &&
                findItemTaskGroups.user_profile_id !=
                this.getCurrentUserProfileId()
            )
                this.selected_member = this.profiles_with_tasks[0];
        },
        addItemToNewTaskGroup(profile_id, responce) {
            const assign_responce = responce;

            assign_responce.data.items = [];
            assign_responce.data.item_ids.forEach((item_id) => {
                const bundle = getSpaceBoardForItemFromTree(item_id);
                if (bundle.item) {
                    assign_responce.data.items.push(bundle);
                }
            });

            const find_own_profile_team_panel_data = this.team_panel_data.find(
                (profile) => profile.user_profile_id == profile_id,
            );

            if (find_own_profile_team_panel_data)
                return find_own_profile_team_panel_data.data.push(
                    assign_responce.data,
                );

            const assigned_user_profile = useUserStore().profiles.find(
                (profile) => profile.id == profile_id,
            );
            const profileData = this.prepareProfileData(assigned_user_profile);
            profileData?.data?.push(assign_responce.data);
            this.pushProfileToTeamPanel(profileData);
            //this.setSubordinates(profileData.user_profile_id)
        },
        addItemToProfileTaskGroup(item_id, profile_id, responce) {
            if (this.team_panel_data.length == 0) return;

            this.removeItemFromTaskGroup(item_id);

            const findAssignedGroup =
                this.byUserProfileAssignedTaskGroup(profile_id);

            if (findAssignedGroup) {
                findAssignedGroup.item_ids.push(item_id);
                findAssignedGroup.items.push(
                    getSpaceBoardForItemFromTree(item_id),
                );
                return;
            }

            this.addItemToNewTaskGroup(profile_id, responce);
        },
        removeItemFromUserTaskItemGroup(user_task_group, item_id) {
            const assigned_group_item_id_index =
                user_task_group?.item_ids?.findIndex(
                    (task_item_id) => task_item_id == item_id,
                );
            if (assigned_group_item_id_index >= 0) {
                user_task_group?.item_ids.splice(
                    assigned_group_item_id_index,
                    1,
                );
            }

            const assigned_group_item_index = user_task_group?.items?.findIndex(
                (item) => item?.item?.id == item_id,
            );
            if (assigned_group_item_index >= 0) {
                user_task_group?.items.splice(assigned_group_item_index, 1);
            }
        },

        unassignItemToProfileTaskGroup(item_id, profile_id) {
            const findItemTaskGroups = this.findItemByUserProfileTasks(
                item_id,
                profile_id,
            );

            if (findItemTaskGroups?.data.length > 0) {
                // const assignedGroup = findItemTaskGroups.data[0]
                this.removeItemFromUserTaskItemGroup(
                    findItemTaskGroups.data[0],
                    item_id,
                );
            }

            if (
                findItemTaskGroups?.data?.filter(
                    (task_group) =>
                        task_group?.items.filter((task) => task).length,
                ).length == 0 &&
                findItemTaskGroups.user_profile_id !=
                this.getCurrentUserProfileId()
            )
                this.selected_member = this.profiles_with_tasks[0];
        },
        removeItemFomTaskGroups(item_id) {
            const findAssignedGroup = this.findItemProfileTasks(item_id);
            if (findAssignedGroup?.data.length > 0) {
                findAssignedGroup.data.forEach((group) =>
                    this.removeItemFromUserTaskItemGroup(group, item_id),
                );
            }
        },
        addItemToTaskGroup(user_profile_id, item, data) {
            if (!data?.user_profile_id) return;

            this.getUserTasks();

            if (data.action == "assign")
                this.addItemToProfileTaskGroup(item?.id, user_profile_id, data);

            if (data.action == "un-assign")
                this.unassignItemToProfileTaskGroup(
                    item?.id,
                    user_profile_id,
                    data,
                );
        },
        addLoginProfileTeamPanelData(profile_data) {
            const profileData = {
                ...this.prepareProfileData(profile_data, profile_data?.groups),
                tasks: [],
                is_login: true,
            };
            this.pushProfileToTeamPanel(profileData);
            //this.setSubordinates(profileData.user_prosfile_id)
        },
        getProfileActiveTask(user_profile_id) {
            const profile = this.getAllProfiles().find(
                (profile) => profile.id == user_profile_id,
            );
            if (profile?.active_task) return profile?.active_task;

            return null;
        },
        getProfilePausedTask(user_profile_id) {
            const profile = this.getAllProfiles().find(
                (profile) => profile.id == user_profile_id,
            );
            if (profile?.paused_task) return profile?.paused_task;

            return null;
        },
        getProfileActiveTaskItem(user_profile_id) {
            const profile = this.getAllProfiles().find(
                (profile) => profile.id == user_profile_id,
            );
            if (profile?.active_task) return profile.active_task;

            return null;
        },
        /**
         * Node in `user_task_groups_new` (/team API tree) for a manager profile.
         * Subordinate order on that node matches {@see TeamDashboardMemberSort} on the backend.
         */
        findNodeInTaskTree(profile_id) {
            const targetId = Number(profile_id);

            const dfs = (node) => {
                if (!node) return null;
                const nodeId = Number(node.user_profile_id ?? node.id);
                if (nodeId === targetId) return node;
                for (const sub of node.subordinates ?? []) {
                    const found = dfs(sub);
                    if (found) return found;
                }
                return null;
            };

            // First search in the current context (fast path / priority)
            const currentRoot = this.user_task_groups_new?.data ?? this.user_task_groups_new;
            if (currentRoot) {
                const found = dfs(currentRoot);
                if (found) return found;
            }

            // Fallback: search in any cached tree in the map
            for (const root of this.team_panel_data_map.values()) {
                if (!root) continue;
                const rootData = root.data ?? root;
                const found = dfs(rootData);
                if (found) return found;
            }

            return null;
        },
        getSubordinatesByProfile(profile_id) {
            const managerId = Number(profile_id);
            const taskTreeNode = this.findNodeInTaskTree(managerId);

            if (taskTreeNode?.subordinates?.length) {
                const orderedIds = taskTreeNode.subordinates
                    .filter((sub) => sub.relation !== "hr")
                    .map((sub) => Number(sub.user_profile_id ?? sub.id))
                    .filter(
                        (id) =>
                            !Number.isNaN(id) &&
                            id !== managerId,
                    );

                if (orderedIds.length > 0) {
                    return useUserStore().getProfilesByIds(
                        orderedIds,
                        true,
                    );
                }
            }

            // Fallback: check flat subordinates mapping
            /*  const fallbackSub = this.subordinates.find(
                  (sub) => Number(sub.assigner_id) === managerId,
              );
              if (fallbackSub?.assignees?.length) {
                  const fallbackIds = fallbackSub.assignees
                      .map((id) => Number(id))
                      .filter((id) => !Number.isNaN(id) && id !== managerId);

                  if (fallbackIds.length > 0) {
                      return useUserStore().getProfilesByIds(
                          fallbackIds,
                          true,
                      );
                  }
              }
  */
            return [];
        },
        /**
         * Whether this profile has task assignees (can show a downline list).
         *
         * @param {number|string|null|undefined} profile_id
         */
        profileHasAssignableDownline(profile_id) {
            if (profile_id == null || profile_id === "") {
                return false;
            }

            return this.getSubordinatesByProfile(profile_id).length > 0;
        },
        setSubordinates(profile_id) {
            //can be useful in future to calculate subordinate without calling api
            return;
            const task_profile = this.byUserProfileTask(profile_id);
            const subordinate_ids = new Set();
            const subordinates = [];

            task_profile.data.forEach((task_group) => {
                task_group.items.forEach((task) => {
                    if (task?.version?.user_profile_id_creator)
                        subordinate_ids.add(
                            task.version.user_profile_id_creator,
                        );
                });
            });

            subordinate_ids.forEach((assigner_profile_id) => {
                const user_profile = this.getAllProfiles().find(
                    (profile) => profile.id == task_profile.user_profile_id,
                );

                if (user_profile) subordinates.push(user_profile);
            });

            task_profile.subordinates = subordinates;
        },
        prepareProfileData(profile, data = [], root_order = 0) {
            if (!profile?.id) return {};
            return {
                id: profile.id,
                full_name: profile.full_name,
                email: profile.email,
                avatar_url: profile.avatar_url,
                user_profile_id: profile.id,
                is_force_app_shift: profile.is_force_app_shift,
                is_active: profile.is_active,
                root_order,
                data,
            };
        },

        prepareTeamPanel(task_data = []) {
            this.addLoginProfileTeamPanelData(task_data);

            if (this.getAllProfiles().length === 0) {
                return;
            }

            this.generateTeamPanelData(task_data?.subordinates);

            this.finalizeTeamPanel();
        },
        generateTeamPanelData(task_data, level = 1) {
            if (!Array.isArray(task_data))
                return console.log("no Teampanel data to process");

            task_data.forEach((data) => {
                const user_profile = this.getUserProfileById(
                    data?.user_profile_id,
                );
                if (!user_profile) return;

                this.processProfileData(user_profile, data?.groups, level + 1);

                this.processSubordinates(data?.subordinates ?? [], level + 2);
            });
        },

        getUserProfileById(profileId) {
            return this.getAllProfiles().find((up) => up.id == profileId);
        },

        processProfileData(user_profile, groups, level) {
            const existingPanelData = this.findProfileInTeamPanel(
                user_profile.id,
            );
            if (existingPanelData) {
                existingPanelData.data = groups;
                return;
            }

            const profileData = this.prepareProfileData(
                user_profile,
                groups,
                level,
            );
            this.pushProfileToTeamPanel(profileData);
        },

        processSubordinates(subordinates, level) {
            subordinates.forEach((subData) => {
                const user_profile = this.getUserProfileById(
                    subData?.user_profile_id,
                );
                if (!user_profile) return;

                this.processProfileData(user_profile, subData?.groups, level);
            });
        },

        findProfileInTeamPanel(profileId) {
            return this.team_panel_data.find(
                (profile) => profile.user_profile_id == profileId,
            );
        },

        finalizeTeamPanel() {
            this.pushTeamHierarchyMember(this.getCurrentProfile());
            this.restoreLastSelection();
            this.reorderTeamPanel();
            this.team_panel_data_prepared = true;
            this.team_hierarchy = this.getUniqueByUserProfileId(
                this.team_hierarchy,
            );
        },
        getUniqueByUserProfileId(arr = []) {
            const seen = new Set();
            return arr.filter((item) => {
                if (seen.has(item.user_profile_id)) {
                    return false;
                }
                seen.add(item.user_profile_id);
                return true;
            });
        },
        restoreLastSelection() {
            if (!this.profiles_with_tasks.length) return;

            const team_selection = useAppState().team;
            if (team_selection?.selected_user_id) {
                const profile = this.getAllProfiles().find(
                    (profile) =>
                        profile.user_profile_id ===
                        team_selection?.selected_user_id,
                );
                let subordinate = {};

                if (team_selection?.team_hierarchy.length) {
                    this.team_hierarchy = [];
                }

                team_selection?.team_hierarchy.forEach((id, index) => {
                    if (id === team_selection.assigner_id) {
                        let team_group_count = index;
                        if (team_selection.selected_subordinate_id) {
                            team_group_count--;
                        }

                        this.team_count = team_group_count;
                    }

                    const mem_data = this.getAllProfiles().find(
                        (mem) => mem.id === id,
                    );

                    if (mem_data) {
                        this.pushTeamHierarchyMember(mem_data);
                    }
                });

                if (team_selection?.selected_subordinate_id)
                    subordinate = this.getAllProfiles().find(
                        (profile) =>
                            profile.user_profile_id ===
                            team_selection?.selected_subordinate_id,
                    );

                if (
                    team_selection?.is_subordinate_panel &&
                    team_selection?.assigner_id
                ) {
                    const assigner = this.getAllProfiles().find(
                        (profile) =>
                            profile.user_profile_id ===
                            team_selection?.assigner_id,
                    );
                    this.onDrillDown(assigner);
                    this.setSelectedTeamMember(profile);
                    return;
                }

                this.setSelectedTeamMember(profile, subordinate);
                return;
            }
            this.setSelectedTeamMember(this.profiles_with_tasks[0]);
        },

        reorderTeamPanel() {
            const current_profile_id = this.getCurrentUserProfileId();
            const my_assigners = this.getAssignersByProfile(current_profile_id);

            const team_panel_data = this.team_panel_data.map((profile) => {
                if (profile.user_profile_id === current_profile_id) {
                    profile.order = 1;
                } else if (my_assigners.has(profile.user_profile_id)) {
                    profile.order = 2;
                } else {
                    profile.order = 3;
                }
                return profile;
            });

            this.team_panel_data = team_panel_data;
        },
        initializeTeamFilter() {
            const current_filter = useAppState().team_filter;

            if (current_filter === "all" || !current_filter) return;

            this.team_filter = this.team_filter.map((filter) => {
                filter.checked = false;

                if (filter.key === current_filter) filter.checked = true;

                return filter;
            });
        },
        resetTeamFilterToDefault() {
            this.team_filter = [
                { key: "shift-in", text: "Shifted In", checked: true },
                { key: "shift-out", text: "Shifted Out", checked: true },
            ];
            useAppState().updateTeamFilter("all");
        },
        toggleSubordinates(value = null) {
            if (value === null)
                return (this.team_panel_subordinates =
                    !this.team_panel_subordinates);

            this.team_panel_subordinates = value;
        },
        updateCurrentSpaceWithNew(space) {
            if (!space || !space.id) return;
            this.team_panel_data.filter((profile) =>
                profile?.data?.filter((task_group) =>
                    task_group?.items?.filter((task_item) => {
                        if (task_item.space_id === space.id) {
                            task_item.space_name = space?.name;
                            this.updateKeyForSourceAndDestination(task_item);
                        }
                    }),
                ),
            );
            this.filterDataOnLiveSync();

            // Sync cached trees
            for (const [contextId, tree] of this.team_panel_data_map.entries()) {
                if (!tree) continue;
                const rootData = tree.data ?? tree;
                const dfs = (node) => {
                    if (!node) return;
                    if (Array.isArray(node.groups)) {
                        node.groups.forEach((group) => {
                            if (Array.isArray(group.items)) {
                                group.items.forEach((task_item) => {
                                    if (Number(task_item.space_id) === Number(space.id)) {
                                        task_item.space_name = space.name;
                                    }
                                });
                            }
                        });
                    }
                    for (const sub of node.subordinates ?? []) {
                        dfs(sub);
                    }
                };
                dfs(rootData);
            }
        },
        updateCurrentBoardWithNew(board, workspace = null) {
            if (!board || !board.id) return;
            this.team_panel_data?.filter((profile) =>
                profile?.data?.filter((task_group) =>
                    task_group?.items?.filter((task_item) => {
                        if (task_item.board_id == board.id) {
                            task_item.board_name = board?.name;
                            if (workspace) {
                                task_item.space_id = workspace?.id;
                                task_item.space_name = workspace?.name;
                            }
                            this.updateKeyForSourceAndDestination(task_item);
                        }
                    }),
                ),
            );
            this.filterDataOnLiveSync();

            // Sync cached trees
            for (const [contextId, tree] of this.team_panel_data_map.entries()) {
                if (!tree) continue;
                const rootData = tree.data ?? tree;
                const dfs = (node) => {
                    if (!node) return;
                    if (Array.isArray(node.groups)) {
                        node.groups.forEach((group) => {
                            if (Array.isArray(group.items)) {
                                group.items.forEach((task_item) => {
                                    if (Number(task_item.board_id) === Number(board.id)) {
                                        task_item.board_name = board.name;
                                        if (workspace) {
                                            task_item.space_id = workspace.id;
                                            task_item.space_name = workspace.name;
                                        }
                                    }
                                });
                            }
                        });
                    }
                    for (const sub of node.subordinates ?? []) {
                        dfs(sub);
                    }
                };
                dfs(rootData);
            }
        },
        updateItemNameByItemId(item) {
            if (item?.id == null) {
                return;
            }

            const name = item?.name;

            this.team_panel_data.filter((profile) =>
                profile?.data?.filter((task_group) =>
                    task_group?.items?.filter((task_item) => {
                        if (task_item.item_id == item.id) {
                            task_item.item_name = name;
                            if (task_item.item) {
                                task_item.item.name = name;
                            }
                        }
                    }),
                ),
            );

            this.selected_member?.tasks?.data?.filter((task_group) =>
                task_group?.items?.filter((task_item) => {
                    if (task_item.item_id == item.id) {
                        task_item.item_name = name;
                        if (task_item.item) {
                            task_item.item.name = name;
                        }
                        this.updateNodeKey(task_item);
                    }
                }),
            );

            // Sync cached trees
            for (const [contextId, tree] of this.team_panel_data_map.entries()) {
                if (!tree) continue;
                const rootData = tree.data ?? tree;
                const dfs = (node) => {
                    if (!node) return;
                    if (Array.isArray(node.groups)) {
                        node.groups.forEach((group) => {
                            if (Array.isArray(group.items)) {
                                const groupItem = group.items.find(gi => Number(gi.item_id) === Number(item.id));
                                if (groupItem) {
                                    groupItem.item_name = name;
                                    if (groupItem.item) {
                                        groupItem.item.name = name;
                                    }
                                }
                            }
                        });
                    }
                    for (const sub of node.subordinates ?? []) {
                        dfs(sub);
                    }
                };
                dfs(rootData);
            }

            useUserStore().updateProfileTask(item);
        },
        updateKeyForSourceAndDestination(source, destination) {
            this.updateNodeKey(destination);
            if (source?.id != destination?.id) this.updateNodeKey(source);
        },
        updateNodeKey(object) {
            if (!object) return;

            object.liveSyncKey = Date.now() + (object.id ?? 10);
        },
        removeItem(item_id) {
            if (this.team_kanban_active_item_id == item_id) {
                this.team_kanban_active_item_id = null;
            }

            this.team_panel_data?.find((profile) => {
                profile?.data?.find((item_group) => {
                    const group_item_index = item_group?.items?.findIndex(
                        (group_item) => group_item.item_id == item_id,
                    );
                    if (group_item_index >= 0)
                        item_group?.items?.splice(group_item_index, 1);
                });
            });
        },

        /**
         * Restore downline route state from `/team/:root/:d1/:d2/.../:tab`.
         *
         * The final segment is the selected profile. The people list should stay
         * opened at that profile's parent level, matching a user click on a
         * visible profile rather than a drilldown into its own list.
         *
         * @param {number|string|null|undefined} rootProfileId Primary list anchor
         * @param {(number|string)[]} segmentProfileIds Downline path segments
         */
        applyRouteDownlineSegmentChain(rootProfileId, segmentProfileIds) {
            console.log("[TeamPanelStore] applyRouteDownlineSegmentChain start", {
                rootProfileId,
                segmentProfileIds,
                current_context: this.getCurrentUserProfileId(),
                team_panel_subordinates: this.team_panel_subordinates,
                sub_level_list: this.sub_level_list.map(s => s?.user_profile_id ?? s?.id)
            });
            if (
                rootProfileId == null ||
                rootProfileId === "" ||
                !segmentProfileIds?.length
            ) {
                return;
            }

            useScreenshotAppState().setSelectedScreenshot([], true);

            const rootRow =
                this.byUserProfileTask(rootProfileId) ??
                this.memberForDownlineRouteDrill(rootProfileId);
            console.log("[TeamPanelStore] applyRouteDownlineSegmentChain rootRow found:", !isEmpty(rootRow));
            if (isEmpty(rootRow)) {
                this.selectActivePeopleForUserProfileId(rootProfileId);
                return;
            }

            this.sub_level_list = [];
            this.team_count = 0;
            this.team_hierarchy = [];
            this.opened_member = {};
            this.new_current_user = null;
            this.team_panel_subordinates = false;
            this.setTeamHierarchy(true, rootRow, 0);
            this.setSelectedTeamMemberById(rootProfileId);

            const routeHierarchy = [rootRow];
            const drillSegmentIds = segmentProfileIds.slice(0, -1);
            const selectedProfileId =
                segmentProfileIds[segmentProfileIds.length - 1];

            console.log("[TeamPanelStore] applyRouteDownlineSegmentChain loop segments:", drillSegmentIds);
            for (const pid of drillSegmentIds) {
                const hasData = this.team_panel_data_map.has(pid);
                if (hasData && !this.profileHasAssignableDownline(pid)) {
                    continue;
                }

                const member = this.memberForDownlineRouteDrill(pid);
                if (member == null || isEmpty(member)) {
                    break;
                }
                this.appendTeamHierarchyMember(routeHierarchy, member);
                this.team_count += 1;
                this.setTeamHierarchy(true, member);
                this.onDrillDown(member, false, true);
            }

            let selectedMember =
                this.memberForDownlineRouteDrill(selectedProfileId);

            const hasTasks = this.team_panel_data.some(
                (p) => String(p.user_profile_id ?? p.id) === String(selectedProfileId) && p.data && p.data.length > 0
            );

            console.log("[TeamPanelStore] applyRouteDownlineSegmentChain selectedMember check:", {
                selectedProfileId,
                found_member: !isEmpty(selectedMember),
                hasTasks
            });

            if (selectedMember == null || isEmpty(selectedMember)) {
                this.selectActivePeopleForUserProfileId(selectedProfileId);
            } else {
                this.appendTeamHierarchyMember(routeHierarchy, selectedMember);
                this.team_hierarchy = routeHierarchy;
                this.setSelectedTeamMemberById(selectedProfileId);
            }

            if (!hasTasks && selectedProfileId != null) {
                const contextId = this.getCurrentUserProfileId();
                console.log("[TeamPanelStore] applyRouteDownlineSegmentChain fetching tasks for:", selectedProfileId, "under context:", contextId);
                axios.get(`/team-panel/${selectedProfileId}/${contextId}`)
                    .then((response) => {
                        this.clearForbiddenProfile(selectedProfileId);
                        const taskData = response.data?.data ?? response.data ?? {};
                        this.generateTeamPanelData([taskData], 2);
                        this.setSelectedMemberTasks();
                    })
                    .catch((error) => {
                        if (error.response?.status === 403) {
                            this.markProfileAsForbidden(selectedProfileId);
                        }
                        console.error("[TeamPanelStore] fetch team tasks failed in applyRouteDownlineSegmentChain", error);
                    });
            }
            this.syncTeamPanelDataForContext();
        },

        memberForDownlineRouteDrill(profileId) {
            if (profileId == null || profileId === "") {
                return null;
            }

            const idNorm = String(profileId);
            const profile = this.getAllProfiles().find(
                (m) => String(m.user_profile_id ?? m.id) === idNorm,
            );
            if (!isEmpty(profile)) {
                const uid = profile.user_profile_id ?? profile.id;
                return { ...profile, user_profile_id: uid };
            }

            const taskRow = this.byUserProfileTask(profileId);
            if (!isEmpty(taskRow)) {
                return taskRow;
            }

            return null;
        },

        onDrillDown(
            subordinate,
            check_sub_level = false,
            push_to_sub_level = true,
        ) {
            console.log("[TeamPanelStore] onDrillDown start (sync)", {
                subordinate: { id: subordinate?.id, user_profile_id: subordinate?.user_profile_id, full_name: subordinate?.full_name },
                check_sub_level,
                push_to_sub_level,
                current_context: this.getCurrentUserProfileId(),
                team_panel_subordinates: this.team_panel_subordinates
            });
            this.team_panel_subordinates = true;
            this.new_current_user = subordinate.user_profile_id;
            this.setSelectedTeamMemberById(subordinate.user_profile_id);
            this.opened_member = subordinate;

            if (check_sub_level) {
                this.sub_level_list = [];
            }

            if (push_to_sub_level) {
                this.sub_level_list.push(subordinate);
            }

            this.reorderTeamPanel();

            const contextId = subordinate.user_profile_id;
            console.log("[TeamPanelStore] onDrillDown contextId check", {
                contextId,
                has_in_map: this.team_panel_data_map.has(contextId)
            });
            if (!this.team_panel_data_map.has(contextId)) {
                this.team_panel_data_prepared = false;
                console.log("[TeamPanelStore] onDrillDown fetching API in background...", `/team-panel/${contextId}/${contextId}`);
                axios.get(`/team-panel/${contextId}/${contextId}`)
                    .then((response) => {
                        this.clearForbiddenProfile(contextId);
                        const responseData = response.data?.data ?? {};
                        this.team_panel_data_map.set(contextId, responseData);
                        this.prefetchSubordinates(responseData);
                        this.team_panel_data_map = new Map(this.team_panel_data_map);
                        console.log("[TeamPanelStore] onDrillDown fetch success, generating team panel data and setting selected tasks");
                        this.syncTeamPanelDataForContext();
                        this.setSelectedMemberTasks();
                    })
                    .catch((error) => {
                        if (error.response?.status === 403) {
                            this.markProfileAsForbidden(contextId);
                        }
                        console.error("[TeamPanelStore] fetch team panel failed on drill down", error);
                    })
                    .finally(() => {
                        this.team_panel_data_prepared = true;
                        console.log("[TeamPanelStore] onDrillDown API fetch finished, team_panel_data_prepared set to true");
                    });
            } else {
                console.log("[TeamPanelStore] onDrillDown cache hit, setting selected tasks directly");
                this.team_panel_data_prepared = true;
                this.syncTeamPanelDataForContext();
                this.setSelectedMemberTasks();
            }
            console.log("[TeamPanelStore] onDrillDown finished (sync execution done)", {
                new_context: this.getCurrentUserProfileId(),
                team_panel_subordinates: this.team_panel_subordinates,
                sub_level_list_length: this.sub_level_list.length
            });
        },
        filterDataOnLiveSync() {
            this.setSelectedMemberTasks();
        },
        hasProfileActiveTask(assignee_id, assigner_id) {
            return Boolean(
                this.team_panel_data
                    .find((profile) => profile.user_profile_id === assignee_id)
                    ?.data?.filter(
                    (group) =>
                        group?.items?.filter(
                            (task) =>
                                task?.item?.version
                                    ?.user_profile_id_creator ===
                                assigner_id,
                        )?.length > 0,
                )?.length,
            );
        },
        hasProfileActiveTaskByAssignee(assignee_id, assigner_id) {
            return this.subordinates
                .find((subordinate) => subordinate.assigner_id === assigner_id)
                ?.assignees?.includes(assignee_id);
        },
        /**
         * All profile IDs in a manager's reports-to downline (from `/user-profile-tasks/subordinates`).
         *
         * @param {number|string|null} [rootProfileId]
         * @returns {Set<number>}
         */
        collectReportsToDownlineProfileIds(rootProfileId = null, maxDepth = 50) {
            const rootId = Number(
                rootProfileId ??
                this.getCurrentProfile()?.id ??
                useUserStore().profile?.id,
            );

            return collectReportsToDownlineProfileIds(
                this.subordinates,
                rootId,
                maxDepth,
            );
        },
        /**
         * Whether `profileId` is in the logged-in user's reports-to hierarchy (team tree or subordinates API).
         *
         * @param {number|string} profileId
         * @param {number|string|null} [rootProfileId]
         */
        isReportsToDownlineProfile(profileId, rootProfileId = null) {
            const targetId = Number(profileId);
            const rootId = Number(
                rootProfileId ??
                this.getCurrentProfile()?.id ??
                useUserStore().profile?.id,
            );
            if (
                !targetId ||
                Number.isNaN(targetId) ||
                !rootId ||
                targetId === rootId
            ) {
                return false;
            }

            const path = this.findPathInTaskTree(targetId);
            if (
                Array.isArray(path) &&
                path.length >= 2 &&
                Number(path[0]) === rootId
            ) {
                return true;
            }

            return this.collectReportsToDownlineProfileIds(rootId).has(
                targetId,
            );
        },
        onPreviousAssigner() {
            this.sub_level_list.pop();
            if (this.sub_level_list?.length) {
                const subordinate =
                    this.sub_level_list[this.sub_level_list.length - 1];
                if (subordinate) {
                    this.setSelectedTeamMemberById(subordinate.user_profile_id);
                    this.new_current_user = subordinate.user_profile_id;
                    this.team_panel_subordinates = true;
                    this.opened_member = subordinate;

                    this.reorderTeamPanel();
                } else {
                    this.resetAssigner();
                }
            } else {
                this.resetAssigner();
            }
            this.syncTeamPanelDataForContext();
        },
        resetAssigner() {
            this.setPinnedTeamMember(null);

            this.toggleSubordinates(false);
            this.onEmptySelectedMemberList();
            this.reorderTeamPanel();
        },

        setHasTeamActiveItem(val) {
            this.has_team_active_item = val;
        },

        /**
         * @param {number|string|null} id Workspace item id
         */
        setTeamKanbanActiveItemId(id) {
            this.team_kanban_active_item_id = id;
            if (id != null && id !== "") {
                useWorkspaceTree().setActiveItemId(id, false);
            }
        },

        clearTeamKanbanActiveItem() {
            this.team_kanban_active_item_id = null;
            useWorkspaceTree().setActiveItemId(null);
        },

        async getTeamWorkItemsBaseUrl(item) {
            const id = item?.id;
            if (!id) {
                return null;
            }
            const workspaceStore = useWorkspace();
            const payload =
                await workspaceStore.fetchSpaceBoardPayloadForItem(id);
            const space = payload?.workspace;
            const board = payload?.board;
            if (!space?.slug || !board?.slug) {
                return null;
            }

            return `${workspaceStore.prepareUrlWithSpaceAndBoard(space, board)}/items`;
        },

        async endAssignment(item, ctr = 0) {
            const { data } = await axios.get(
                `/user-profile-tasks/items/${item.id}/end-assignment`,
            );
            this.removeItem(item.id);
            useTimeTrackerStore().removePauseTaskOnTaskUnAssigned(
                item.id,
                useUserStore().profile.id,
            );
            const tree = useWorkspaceTree();
            const treeItem = tree.getItemById(item.id);
            if (treeItem) {
                tree.syncItemById(item.id, {
                    version: {
                        ...(treeItem.version ?? {}),
                        user_profile_id_assignee: null,
                        current_assignment_status: null,
                    },
                });
            }

            const { toggleSnackBar } = Utilities();
            timeOutHelper(() => {
                toggleSnackBar({
                    title: item.name,
                    message: `Item assignment ended.`,
                    timeout: 3000,
                    color: "success",
                    location: "bottom right",
                });
            }, 3000 * ctr);
        },

        clearScreenshotsData() {
            this.screenshotsData.fetchId += 1;
            this.screenshotsData.data = [];
            this.screenshotsData.hasMore = false;
            this.screenshotsData.loading = false;
            this.screenshotsData.loadingMore = false;
            useScreenshotAppState().setSelectedScreenshot([], true);
        },

        syncScreenshotsAccessFromPermissions() {
            if (!this.can_access_screenshot_tab) {
                this.clearScreenshotsData();
            }
        },

        isCurrentScreenshotFetch(fetchId, payload) {
            if (fetchId !== this.screenshotsData.fetchId) {
                return false;
            }

            return !isStaleScreenshotProfileResponse({
                requestedProfileId: payload?.profile_id,
                selectedSubordinateId:
                    this.selected_subordinate?.user_profile_id,
                selectedMemberId: this.selected_member?.user_profile_id,
            });
        },

        async loadScreenshotsData(payload, push = false) {
            if (!this.can_access_screenshot_tab) {
                this.clearScreenshotsData();

                return;
            }
            const isLoadingMore = Boolean(payload?.skip);
            const isReplace = !isLoadingMore && !push;
            const fetchId = isReplace
                ? (this.screenshotsData.fetchId += 1)
                : this.screenshotsData.fetchId;

            if (isReplace) {
                this.screenshotsData.data = [];
                this.screenshotsData.loading = true;
            } else {
                this.screenshotsData.loadingMore = true;
            }
            try {
                const res = await axios.post(
                    "/user-profile-tasks/screenshots",
                    {
                        limit: this.screenshotsData.LIMIT,
                        ...payload,
                    },
                );

                if (!this.isCurrentScreenshotFetch(fetchId, payload)) {
                    return;
                }

                const newScreenshots = res.data.data ?? [];

                if (isLoadingMore || push) {
                    this.screenshotsData.data.push(...newScreenshots);
                } else {
                    this.screenshotsData.data = newScreenshots;
                }

                this.screenshotsData.hasMore =
                    newScreenshots.length === this.screenshotsData.LIMIT;
            } catch (error) {
                console.log({ error });
            } finally {
                if (fetchId !== this.screenshotsData.fetchId) {
                    return;
                }
                this.screenshotsData.loading = false;
                this.screenshotsData.loadingMore = false;
            }
        },
        async getShiftOverview(member_id) {
            const user_store = useUserStore();
            let shift_overview = [];

            try {
                const response = await axios.post("/reports/shifts", {
                    user_profile_ids: [member_id],
                    by: "day",
                    timezone: DateUtilities.getTimezone(),
                    date: DateUtilities.getStartAndEndDateFromToday(-7),
                });
                shift_overview = response.data?.data;
            } catch (error) {
                console.log({ error });
            }

            return shift_overview;
        },

        isActiveItemForProfile(profile_id, item_id) {
            return this.getProfileActiveTask(profile_id)?.id === item_id;
        },
        isPausedItemForProfile(profile_id, item_id) {
            return this.getProfilePausedTask(profile_id)?.id === item_id;
        },
        setTeamPanelDatePrepared(value) {
            this.team_panel_data_prepared = value;
        },
        updateActiveUserTaskByTaskId(task_id, data) {
            this.selected_member?.tasks?.data.forEach((group) => {
                group?.items?.forEach((task) => {
                    if (task.item_id === task_id) {
                        task.item.name = data.name;
                        task.item.slug = data.slug;
                    }
                });
            });
        },
        isSelfAssigned(assigner_id, item) {
            return assigner_id == item.version?.user_profile_id_assignee;
        },
        async updateForceUseOfApp(
            status,
            selected_member = this.selected_member,
        ) {
            try {
                if (!selected_member) {
                    throw new Error("selected_member is invalid");
                }

                await axios.patch(
                    `/profiles/${selected_member.user_profile_id}/force-app`,
                    { is_force_app_shift: status },
                );
                const profile = this.getAllProfiles().find(
                    (profile) =>
                        profile.user_profile_id ===
                        selected_member.user_profile_id,
                );
                profile.is_force_app_shift = status;
                selected_member.is_force_app_shift = status;
                //this.selected_member.is_force_app_shift = status;
                Utilities().showSuccessSnackbar("Updated successfully.");
            } catch (e) {
                utilities().handleError(e);
            }
        },
        updateProfileTaskItems(profile_id, cat_index, value) {
            const user_index = this.team_panel_data.findIndex(
                (task) => task.user_profile_id == profile_id,
            );
            let current_member = cloneDeep(this.team_panel_data[user_index]);

            current_member.data[cat_index] = {
                items: value,
                ...current_member.data[cat_index],
            };

            this.selected_member.tasks.data[cat_index] =
                current_member.data[cat_index];
            this.team_panel_data.splice(user_index, 1, current_member);
        },
        updateProfileTaskData(profile_id, data) {
            this.team_panel_data.forEach((task) => {
                if (task.user_profile_id == profile_id) {
                    task.data = data;
                }
            });
        },
        updateProfileTaskCategory(profile_id, cat_index, category_data) {
            const user_index = this.team_panel_data.findIndex(
                (task) => task.user_profile_id == profile_id,
            );

            if (
                user_index >= 0 &&
                this.team_panel_data[user_index].data[cat_index]
            ) {
                // Merge category data while preserving existing properties
                const updated_category = {
                    ...this.team_panel_data[user_index].data[cat_index],
                    ...category_data,
                };

                // Update in team_panel_data
                this.team_panel_data[user_index].data.splice(
                    cat_index,
                    1,
                    updated_category,
                );

                // Also update in selected_member.tasks if it exists
                if (
                    this.selected_member?.tasks?.data &&
                    this.selected_member.tasks.data[cat_index]
                ) {
                    this.selected_member.tasks.data.splice(
                        cat_index,
                        1,
                        updated_category,
                    );
                }
            }
        },
        async deleteScreenshot(screenshot_ids, user_profile_id) {
            try {
                await axios.delete("/user-profile-tasks/screenshots", {
                    data: {
                        user_profile_id,
                        ids: screenshot_ids,
                    },
                });
                screenshot_ids?.forEach((screenshot_id) => {
                    const findedScreenshot = this.screenshotsData.data.find(
                        (screenshot) => screenshot.id === screenshot_id,
                    );
                    if (findedScreenshot) {
                        findedScreenshot.deleted_at = new Date().toISOString();
                        findedScreenshot.deleted_by = user_profile_id;
                    }
                });
            } catch (error) {
                console.log("failed to delete screenshots");
            }
        },
        generateImageTags(screenshot_ids = []) {
            const image_tags = screenshot_ids.map((scrnshot_id) => {
                const findedScreenshot = this.screenshotsData.data.find(
                    (screenshot) => screenshot.id === scrnshot_id,
                );
                console.log(findedScreenshot);
                let image_tag = ``;
                if (findedScreenshot) {
                    //image_tag = `<img src='${findedScreenshot.image_path}' alt='screenshot-${findedScreenshot.id}' />`;
                    // const image = document.createElement('img');
                    // image.src = findedScreenshot.image_path;
                    // image.alt = `screenshot-${findedScreenshot.id}`;
                    // image_tag = image.outerHTML;
                }
                return image_tag;
            });
            return image_tags.join(" ");
        },

        async getImageBlobData(data) {
            const { saveData, getData } = useDexieCrud(TABLES.SCREENSHOTS);
            const abortController = new AbortController();

            const response = await getData(data.id);

            if (response?.error) {
                throw new Error(response.data ?? "No image found");
            }

            let blob = response.data?.source ?? null;
            let name =
                data?.file_name ??
                `${Date.now()}.${blob?.type?.split("/")[1]}` ??
                "";
            if (!blob) {
                const response = await getImageUrlToBlob(
                    data.image_path,
                    abortController.signal,
                );
                if (response?.error) {
                    throw new Error(response.data ?? "No image found");
                }
                blob = response.data;
                name =
                    data?.file_name ??
                    `${Date.now()}.${blob?.type?.split("/")[1]}` ??
                    "";
                saveData(
                    {
                        id: data.id,
                        name: name,
                        source: blob,
                    },
                    data.id,
                );
            }

            return { blob, name };
        },

        getTeamHierarchyMemberId(member) {
            if (member == null) {
                return null;
            }

            return member.user_profile_id ?? member.id ?? null;
        },

        isSameTeamHierarchyMember(a, b) {
            const id_a = this.getTeamHierarchyMemberId(a);
            const id_b = this.getTeamHierarchyMemberId(b);

            if (id_a == null || id_b == null) {
                return false;
            }

            return String(id_a) === String(id_b);
        },

        isDuplicateOfPreviousHierarchyMember(member) {
            const previous =
                this.team_hierarchy[this.team_hierarchy.length - 1];

            return this.isSameTeamHierarchyMember(previous, member);
        },

        pushTeamHierarchyMember(member) {
            if (this.isDuplicateOfPreviousHierarchyMember(member)) {
                return false;
            }

            this.team_hierarchy.push(member);

            return true;
        },

        appendTeamHierarchyMember(hierarchy, member) {
            const previous = hierarchy[hierarchy.length - 1];

            if (this.isSameTeamHierarchyMember(previous, member)) {
                return false;
            }

            hierarchy.push(member);

            return true;
        },

        setTeamHierarchy(is_base_member, member, team_count = this.team_count) {
            let base_index = 0;

            if (is_base_member) {
                base_index = team_count;
            } else {
                const parent = this.team_panel_subordinates
                    ? this.opened_member
                    : this.getCurrentProfile();
                const parentIndex = parent
                    ? this.team_hierarchy.findLastIndex((m) =>
                          this.isSameTeamHierarchyMember(m, parent),
                      )
                    : -1;

                if (parentIndex >= 0) {
                    base_index = parentIndex + 1;
                } else {
                    base_index = team_count + 1;
                }
            }

            const previous =
                base_index > 0 ? this.team_hierarchy[base_index - 1] : null;

            if (this.isSameTeamHierarchyMember(previous, member)) {
                this.team_hierarchy.splice(
                    base_index,
                    this.team_hierarchy.length,
                );

                return;
            }

            this.team_hierarchy.splice(
                base_index,
                this.team_hierarchy.length,
                member,
            );
        },

        resetLastTeamMemberLocate() {
            this.last_team_member_locate = {
                assigner_id: null,
                assignee_id: null,
            };
        },

        setLastTeamMemberLocateAssigner(profile_id) {
            profile_id =
                profile_id ||
                useUserStore().user_data?.profile?.user_profile_id;

            this.last_team_member_locate.assigner_id = profile_id;
        },

        setLastTeamMemberLocateAssignee(profile_id) {
            this.last_team_member_locate.assignee_id = profile_id;
        },

        async fetchUserStories(userProfileId) {
            try {
                this.isLoadingUserStories = true;
                const response = await axios.get(`/stories/${userProfileId}`);
                this.userStories.set(userProfileId, {
                    access: true,
                    data: response.data?.data ?? [],
                });
            } catch (error) {
                this.userStories.set(userProfileId, {
                    access: error.response?.data?.errors !== 403,
                    data: [],
                });
            } finally {
                this.isLoadingUserStories = false;
            }
        },

        clearUserStories(userProfileId) {
            this.userStories.clear();
        },

        // ─── LiveSync Map Update Actions ────────────────────────────

        // DFS helper to find a profile node in a tree
        findProfileNodeInTree(root, profileId) {
            if (!root) return null;
            const targetId = Number(profileId);
            const dfs = (node) => {
                if (!node) return null;
                const nodeId = Number(node.user_profile_id ?? node.id);
                if (nodeId === targetId) return node;
                for (const sub of node.subordinates ?? []) {
                    const found = dfs(sub);
                    if (found) return found;
                }
                return null;
            };
            return dfs(root);
        },

        // DFS helper to remove a profile node from a tree and return it
        removeProfileNodeFromTree(root, profileId, parentProfileId = null) {
            if (!root) return null;
            const targetId = Number(profileId);
            const parentId = parentProfileId != null ? Number(parentProfileId) : null;
            const dfs = (node) => {
                if (!node || !Array.isArray(node.subordinates)) return null;
                const idx = node.subordinates.findIndex(
                    (sub) => Number(sub.user_profile_id ?? sub.id) === targetId,
                );
                if (idx >= 0) {
                    const nodeParentId = Number(node.user_profile_id ?? node.id);
                    if (parentId === null || nodeParentId === parentId) {
                        return node.subordinates.splice(idx, 1)[0];
                    }
                }
                for (const sub of node.subordinates) {
                    const found = dfs(sub);
                    if (found) return found;
                }
                return null;
            };
            return dfs(root);
        },


        // Helper to find all nodes with a given profile ID (in case of multiple assignment paths)
        findAllProfileNodesInTree(root, profileId) {
            const results = [];
            if (!root) return results;
            const targetId = Number(profileId);
            const dfs = (node) => {
                if (!node) return;
                const nodeId = Number(node.user_profile_id ?? node.id);
                if (nodeId === targetId) {
                    results.push(node);
                }
                for (const sub of node.subordinates ?? []) {
                    dfs(sub);
                }
            };
            dfs(root);
            return results;
        },

        // DFS helper to remove an item from all profiles in a tree
        removeItemFromTree(root, itemId) {
            if (!root) return;
            const id = Number(itemId);
            const dfs = (node) => {
                if (!node) return;
                if (Array.isArray(node.groups)) {
                    node.groups.forEach((group) => {
                        if (Array.isArray(group.items)) {
                            const idx = group.items.findIndex(item => Number(item.item_id) === id);
                            if (idx >= 0) group.items.splice(idx, 1);
                        }
                    });
                }
                for (const sub of node.subordinates ?? []) {
                    dfs(sub);
                }
            };
            dfs(root);
        },



        // LiveSync actions
        syncLiveSyncAssignItem(e) {
            const taskItem = e?.group_item;
            if (!taskItem) return;

            const assigneeProfileId = Number(e?.profile_id);
            if (Number.isNaN(assigneeProfileId)) return;

            const assignerProfileId = Number(taskItem?.item?.version?.user_profile_id_creator);
            const taskItemId = taskItem.item_id;
            const upline = Array.isArray(e?.upline) ? e.upline.map(Number) : [];

            console.log("[TeamPanel] ━━━ ASSIGN START ━━━", {
                taskItemId,
                assigneeProfileId,
                assignerProfileId,
            });

            // 1. Identify affected tree context IDs
            const affectedTreeIds = new Set();

            // Always re-fetch the logged-in user's own tree (root context)
            const loggedInUserId = Number(useUserStore().profile.id);
            if (this.team_panel_data_map.has(loggedInUserId)) {
                affectedTreeIds.add(loggedInUserId);
            }

            // Also re-fetch the currently active UI context if drilled-down
            const activeContextId = Number(this.getCurrentUserProfileId());
            if (this.team_panel_data_map.has(activeContextId)) {
                affectedTreeIds.add(activeContextId);
            }

            // Re-fetch assigner's tree (they might see a new ad-hoc node)
            if (assignerProfileId && !Number.isNaN(assignerProfileId) && this.team_panel_data_map.has(assignerProfileId)) {
                affectedTreeIds.add(assignerProfileId);
            }

            // Re-fetch assignee's tree (they have a new task)
            if (this.team_panel_data_map.has(assigneeProfileId)) {
                affectedTreeIds.add(assigneeProfileId);
            }

            // Re-fetch any tree in the assignee's upline
            for (const upId of upline) {
                if (this.team_panel_data_map.has(upId)) {
                    affectedTreeIds.add(upId);
                }
            }

            console.log("[TeamPanel][TA-REFETCH] Affected trees to re-fetch:", [...affectedTreeIds]);

            // 2. Re-fetch each affected tree in parallel
            const fetchPromises = [...affectedTreeIds].map(async (treeCtxId) => {
                try {
                    console.log(`[TeamPanel][TA-REFETCH] Fetching GET /team-panel/${treeCtxId}/${treeCtxId}`);
                    const response = await axios.get(`/team-panel/${treeCtxId}/${treeCtxId}`);
                    this.clearForbiddenProfile(treeCtxId);
                    const freshData = response.data?.data ?? {};
                    this.team_panel_data_map.set(treeCtxId, freshData);
                    console.log(`[TeamPanel][TA-REFETCH] Success: tree=${treeCtxId} replaced with fresh data`);
                } catch (err) {
                    if (err.response?.status === 403) {
                        console.log(`[TeamPanel][TA-REFETCH] 403 for tree=${treeCtxId} — markProfileAsForbidden`);
                        this.markProfileAsForbidden(treeCtxId);
                    }
                    console.error(`[TeamPanel][TA-REFETCH] Failed to re-fetch tree=${treeCtxId}`, err);
                }
            });

            Promise.all(fetchPromises).then(() => {
                // Task was successfully added — the assignee is accessible, clear any stale forbidden flag
                console.log(`[TeamPanel] clearForbiddenProfile(${assigneeProfileId})`);
                this.clearForbiddenProfile(assigneeProfileId);

                // Trigger reactivity + UI refresh
                this.team_panel_data_map = new Map(this.team_panel_data_map);
                this.syncTeamPanelDataForContext();
                this.setSelectedMemberTasks();
                console.log("[TeamPanel] ━━━ ASSIGN END ━━━");
            });
        },

        syncLiveSyncUnassignItem(e) {
            const itemId = e?.item_id;
            if (!itemId) return;
            const unassignedProfileId = Number(e?.profile_id);
            const assignerProfileId = Number(e?.version?.user_profile_id_creator);
            const upline = Array.isArray(e?.upline) ? e.upline.map(Number) : [];

            console.log("[TeamPanel] ━━━ UNASSIGN START ━━━", { itemId, profileId: unassignedProfileId, assignerProfileId });

            if (Number.isNaN(unassignedProfileId)) {
                // [TU-06] Invalid profile — unrelated event
                console.log(`[TeamPanel][TU-06] SKIP — invalid profile_id in unassign event`);
                return;
            }

            // 1. Identify affected tree context IDs
            const affectedTreeIds = new Set();
            const loggedInUserId = Number(useUserStore().profile.id);

            // Always re-fetch the logged-in user's own tree
            if (this.team_panel_data_map.has(loggedInUserId)) {
                affectedTreeIds.add(loggedInUserId);
            }

            // Also re-fetch the currently active UI context if drilled-down
            const activeContextId = Number(this.getCurrentUserProfileId());
            if (this.team_panel_data_map.has(activeContextId)) {
                affectedTreeIds.add(activeContextId);
            }

            // Re-fetch assigner's tree (ad-hoc node may need pruning)
            if (assignerProfileId && !Number.isNaN(assignerProfileId) && this.team_panel_data_map.has(assignerProfileId)) {
                affectedTreeIds.add(assignerProfileId);
            }

            // Re-fetch unassigned assignee's tree
            if (this.team_panel_data_map.has(unassignedProfileId)) {
                affectedTreeIds.add(unassignedProfileId);
            }

            // Re-fetch any tree in the upline
            for (const upId of upline) {
                if (this.team_panel_data_map.has(upId)) {
                    affectedTreeIds.add(upId);
                }
            }

            console.log("[TeamPanel][TU-REFETCH] Affected trees to re-fetch:", [...affectedTreeIds]);

            // 2. Re-fetch each affected tree in parallel
            const fetchPromises = [...affectedTreeIds].map(async (treeCtxId) => {
                try {
                    console.log(`[TeamPanel][TU-REFETCH] Fetching GET /team-panel/${treeCtxId}/${treeCtxId}`);
                    const response = await axios.get(`/team-panel/${treeCtxId}/${treeCtxId}`);
                    this.clearForbiddenProfile(treeCtxId);
                    const freshData = response.data?.data ?? {};
                    this.team_panel_data_map.set(treeCtxId, freshData);
                    console.log(`[TeamPanel][TU-REFETCH] Success: tree=${treeCtxId} replaced with fresh data`);
                } catch (err) {
                    if (err.response?.status === 403) {
                        console.log(`[TeamPanel][TU-REFETCH] 403 for tree=${treeCtxId} — markProfileAsForbidden`);
                        this.markProfileAsForbidden(treeCtxId);
                    }
                    console.error(`[TeamPanel][TU-REFETCH] Failed to re-fetch tree=${treeCtxId}`, err);
                }
            });

            Promise.all(fetchPromises).then(() => {
                // If the profile was pruned from all trees, mark as forbidden
                // so the UI does not try to show stale backlog data for them
                const isDownline = this.isReportsToDownlineProfile(unassignedProfileId, loggedInUserId);
                const isSelf = unassignedProfileId === loggedInUserId;

                if (!isSelf && !isDownline) {
                    const isStillInAnyTree = [...this.team_panel_data_map.values()].some(tree => {
                        if (!tree) return false;
                        const rootData = tree.data ?? tree;
                        return this.findProfileNodeInTree(rootData, unassignedProfileId) !== null;
                    });

                    if (!isStillInAnyTree) {
                        console.log(`[TeamPanel][TU-05] markProfileAsForbidden(${unassignedProfileId}) — pruned from ALL trees`);
                        this.markProfileAsForbidden(unassignedProfileId);
                    }
                } else {
                    console.log(`[TeamPanel] Profile ${unassignedProfileId} is self or in downline (isSelf: ${isSelf}, isDownline: ${isDownline}) — NOT marking as forbidden`);
                }

                // Trigger reactivity + UI refresh
                this.team_panel_data_map = new Map(this.team_panel_data_map);
                this.syncTeamPanelDataForContext();
                this.setSelectedMemberTasks();
                console.log("[TeamPanel] ━━━ UNASSIGN END ━━━");
            });
        },

        syncLiveSyncMoveItem(e) {
            const itemId = e?.item?.id;
            if (!itemId) return;
            console.log("[TeamPanelStore] syncLiveSyncMoveItem received", { itemId, spaceId: e.workspace?.id, boardId: e.board?.id });
            const updates = {
                space_id: e.workspace?.id,
                space_name: e.workspace?.name,
                board_name: e.board?.name,
                board_id: e.board?.id
            };

            for (const [contextId, tree] of this.team_panel_data_map.entries()) {
                if (!tree) continue;
                const rootData = tree.data ?? tree;

                const dfs = (node) => {
                    if (!node) return;
                    if (Array.isArray(node.groups)) {
                        node.groups.forEach((group) => {
                            if (Array.isArray(group.items)) {
                                const groupItem = group.items.find(item => Number(item.item_id) === Number(itemId));
                                if (groupItem) {
                                    Object.assign(groupItem, updates);
                                }
                            }
                        });
                    }
                    for (const sub of node.subordinates ?? []) {
                        dfs(sub);
                    }
                };
                dfs(rootData);
            }
            this.team_panel_data_map = new Map(this.team_panel_data_map);
            this.syncTeamPanelDataForContext();
            this.setSelectedMemberTasks();
        },

        syncLiveSyncBulkMoveItems(e) {
            if (!e || !e.tasks || !e.user_profile_id) return;
            const taskGroupId = e.task_group_id;
            const profileId = e.user_profile_id;
            const tasksOrderMap = e.tasks;
            console.log("[TeamPanelStore] syncLiveSyncBulkMoveItems received", { taskGroupId, profileId, totalItems: Object.keys(tasksOrderMap).length });

            for (const [contextId, tree] of this.team_panel_data_map.entries()) {
                if (!tree) continue;
                const rootData = tree.data ?? tree;
                const nodes = this.findAllProfileNodesInTree(rootData, profileId);
                nodes.forEach(node => {
                    if (node && Array.isArray(node.groups)) {
                        const allItems = [];
                        node.groups.forEach((group) => {
                            if (Array.isArray(group.items)) {
                                group.items.forEach((item) => {
                                    if (tasksOrderMap.hasOwnProperty(item.item_id)) {
                                        item.order = tasksOrderMap[item.item_id];
                                        item.task_group_id = taskGroupId;
                                    }
                                    allItems.push(item);
                                });
                            }
                        });

                        const grouped = {};
                        allItems.forEach((item) => {
                            if (!grouped[item.task_group_id]) {
                                grouped[item.task_group_id] = [];
                            }
                            grouped[item.task_group_id].push(item);
                        });

                        node.groups.forEach((group) => {
                            const items = grouped[group.id] ?? [];
                            group.items = items.sort((a, b) => a.order - b.order);
                        });
                    }
                });
            }
            this.team_panel_data_map = new Map(this.team_panel_data_map);
            this.syncTeamPanelDataForContext();
            this.setSelectedMemberTasks();
        },

        syncLiveSyncReorderGroups(e) {
            if (!e || !e.task_group_order || !e.profile_id) return;
            const profileId = e.profile_id;
            const taskGroupOrder = e.task_group_order;
            console.log("[TeamPanelStore] syncLiveSyncReorderGroups received", { profileId, taskGroupOrder });

            for (const [contextId, tree] of this.team_panel_data_map.entries()) {
                if (!tree) continue;
                const rootData = tree.data ?? tree;
                const nodes = this.findAllProfileNodesInTree(rootData, profileId);
                nodes.forEach(node => {
                    if (node && Array.isArray(node.groups)) {
                        node.groups.forEach((group) => {
                            group.order = taskGroupOrder[group.id] ?? 0;
                        });
                        node.groups.sort((a, b) => a.order - b.order);
                    }
                });
            }
            this.team_panel_data_map = new Map(this.team_panel_data_map);
            this.syncTeamPanelDataForContext();
            this.setSelectedMemberTasks();
        },

        syncLiveSyncUpdateGroup(e) {
            if (!e || !e.task_group || !e.task_group.user_profile_id) return;
            const taskGroup = e.task_group;
            const profileId = taskGroup.user_profile_id;
            console.log("[TeamPanelStore] syncLiveSyncUpdateGroup received", { groupId: taskGroup.id, groupName: taskGroup.name, profileId });

            for (const [contextId, tree] of this.team_panel_data_map.entries()) {
                if (!tree) continue;
                const rootData = tree.data ?? tree;
                const nodes = this.findAllProfileNodesInTree(rootData, profileId);
                nodes.forEach(node => {
                    if (node && Array.isArray(node.groups)) {
                        const group = node.groups.find(g => Number(g.id) === Number(taskGroup.id));
                        if (group) {
                            group.name = taskGroup.name;
                        }
                    }
                });
            }
            this.team_panel_data_map = new Map(this.team_panel_data_map);
            this.syncTeamPanelDataForContext();
            this.setSelectedMemberTasks();
        },

        syncLiveSyncUpdateItem(e) {
            if (!e || !e.item || !e.item.id) return;
            const updatedItem = e.item;
            const itemId = updatedItem.id;
            console.log("[TeamPanelStore] syncLiveSyncUpdateItem received", { itemId, name: updatedItem.name });

            for (const [contextId, tree] of this.team_panel_data_map.entries()) {
                if (!tree) continue;
                const rootData = tree.data ?? tree;

                const dfs = (node) => {
                    if (!node) return;
                    if (Array.isArray(node.groups)) {
                        node.groups.forEach((group) => {
                            if (Array.isArray(group.items)) {
                                const groupItem = group.items.find(item => Number(item.item_id) === Number(itemId));
                                if (groupItem) {
                                    groupItem.item_name = updatedItem.name;
                                    groupItem.item = updatedItem;
                                }
                            }
                        });
                    }
                    for (const sub of node.subordinates ?? []) {
                        dfs(sub);
                    }
                };
                dfs(rootData);
            }
            this.team_panel_data_map = new Map(this.team_panel_data_map);
            this.syncTeamPanelDataForContext();
            this.setSelectedMemberTasks();
        },

        syncLiveSyncReorderItem(e) {
            const groupItem = e?.group_item;
            if (!groupItem || !groupItem.item_id) return;
            const profileId = Number(e?.profile_id);
            if (Number.isNaN(profileId)) return;
            const targetGroupId = Number(groupItem.task_group_id);
            console.log("[TeamPanelStore] syncLiveSyncReorderItem received", { itemId: groupItem.item_id, profileId, targetGroupId });

            for (const [contextId, tree] of this.team_panel_data_map.entries()) {
                if (!tree) continue;
                const rootData = tree.data ?? tree;

                // 1. Remove the item from any other location in this tree
                this.removeItemFromTree(rootData, groupItem.item_id);

                // 2. Add the item to the assignee's groups
                const nodes = this.findAllProfileNodesInTree(rootData, profileId);
                nodes.forEach(node => {
                    if (node) {
                        if (!node.groups) node.groups = [];
                        let group = node.groups.find(g => Number(g.id) === targetGroupId);
                        if (!group) {
                            group = { id: targetGroupId, name: e.task_group?.name ?? "", items: [] };
                            node.groups.push(group);
                        }
                        if (!group.items) group.items = [];

                        const exists = group.items.some(item => Number(item.item_id) === Number(groupItem.item_id));
                        if (!exists) {
                            group.items.push(groupItem);
                        }

                        group.items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                    }
                });
            }

            this.team_panel_data_map = new Map(this.team_panel_data_map);
            this.syncTeamPanelDataForContext();
            this.setSelectedMemberTasks();
        },

        syncLiveSyncUpdateSpace(space) {
            if (!space || !space.id) return;
            console.log("[TeamPanelStore] syncLiveSyncUpdateSpace received", { spaceId: space.id, spaceName: space.name });
            for (const [contextId, tree] of this.team_panel_data_map.entries()) {
                if (!tree) continue;
                const rootData = tree.data ?? tree;
                const dfs = (node) => {
                    if (!node) return;
                    if (Array.isArray(node.groups)) {
                        node.groups.forEach((group) => {
                            if (Array.isArray(group.items)) {
                                group.items.forEach((task_item) => {
                                    if (Number(task_item.space_id) === Number(space.id)) {
                                        task_item.space_name = space.name;
                                    }
                                });
                            }
                        });
                    }
                    for (const sub of node.subordinates ?? []) {
                        dfs(sub);
                    }
                };
                dfs(rootData);
            }
            this.team_panel_data_map = new Map(this.team_panel_data_map);
            this.syncTeamPanelDataForContext();
            this.setSelectedMemberTasks();
        },

        syncLiveSyncUpdateBoard(board, workspace = null) {
            if (!board || !board.id) return;
            console.log("[TeamPanelStore] syncLiveSyncUpdateBoard received", { boardId: board.id, boardName: board.name });
            for (const [contextId, tree] of this.team_panel_data_map.entries()) {
                if (!tree) continue;
                const rootData = tree.data ?? tree;
                const dfs = (node) => {
                    if (!node) return;
                    if (Array.isArray(node.groups)) {
                        node.groups.forEach((group) => {
                            if (Array.isArray(group.items)) {
                                group.items.forEach((task_item) => {
                                    if (Number(task_item.board_id) === Number(board.id)) {
                                        task_item.board_name = board.name;
                                        if (workspace) {
                                            task_item.space_id = workspace.id;
                                            task_item.space_name = workspace.name;
                                        }
                                    }
                                });
                            }
                        });
                    }
                    for (const sub of node.subordinates ?? []) {
                        dfs(sub);
                    }
                };
                dfs(rootData);
            }
            this.team_panel_data_map = new Map(this.team_panel_data_map);
            this.syncTeamPanelDataForContext();
            this.setSelectedMemberTasks();
        },

        async syncLiveSyncReportingHierarchy(userProfile, oldReportsToId, upline = []) {
            if (!this.team_panel_data_prepared) {
                console.log("[TeamPanel][HR-00] syncLiveSyncReportingHierarchy SKIPPED — team_panel_data not prepared");
                return;
            }

            const xId = Number(userProfile.id ?? userProfile.user_profile_id);

            const currentUserProfileId = this.getCurrentUserProfileId();

            const newParentId = userProfile.reports_to_profile_id ? Number(userProfile.reports_to_profile_id) : null;
            let oldParentId = oldReportsToId !== null && oldReportsToId !== undefined ? Number(oldReportsToId) : oldReportsToId;

            // Normalize upline to a Set of Numbers for fast lookup
            const uplineSet = new Set((upline || []).map(Number));

            if (oldParentId === undefined) {
                // Check local user profiles first for the official reports_to_profile_id
                const profile = useUserStore().profiles.find(p => Number(p.id) === xId);
                if (profile && profile.reports_to_profile_id != null) {
                    oldParentId = Number(profile.reports_to_profile_id);
                    console.log(`[TeamPanel][HR-01] Resolved oldParentId=${oldParentId} from useUserStore().profiles`);
                } else {
                    oldParentId = null;
                }
            }

            // If oldParentId is null (either explicitly passed, or resolved to null above),
            // check if they were an ad-hoc subordinate under a different parent.
            if (!oldParentId) {
                const oldSub = this.subordinates.find(sub =>
                    Array.isArray(sub.assignees) &&
                    sub.assignees.some(id => Number(id) === xId) &&
                    Number(sub.assigner_id) !== newParentId,
                );
                if (oldSub) {
                    oldParentId = Number(oldSub.assigner_id);
                    console.log(`[TeamPanel][HR-01] Resolved oldParentId=${oldParentId} from subordinates flat array (fallback)`);
                } else {
                    console.log(`[TeamPanel][HR-01] Could not resolve oldParentId for profile ${xId}`);
                    oldParentId = null;
                }
            }

            console.log("[TeamPanel] ━━━ HIERARCHY SYNC START ━━━", {
                xId,
                newParentId,
                oldParentId,
                upline: [...uplineSet],
            });

            // 1. Update flat subordinates mapping (reports-to list)
            if (oldParentId) {
                const oldSubRow = this.subordinates.find(sub => Number(sub.assigner_id) === oldParentId);
                if (oldSubRow && Array.isArray(oldSubRow.assignees)) {
                    const originalLength = oldSubRow.assignees.length;
                    oldSubRow.assignees = oldSubRow.assignees.filter(id => Number(id) !== xId);
                    console.log(`[TeamPanel][HR-02] Removed ${xId} from flat subordinates of oldParent ${oldParentId} (${originalLength} → ${oldSubRow.assignees.length})`);
                }
            }

            if (newParentId) {
                let newSubRow = this.subordinates.find(sub => Number(sub.assigner_id) === newParentId);
                if (!newSubRow) {
                    newSubRow = { assigner_id: newParentId, assignees: [] };
                    this.subordinates.push(newSubRow);
                }
                if (Array.isArray(newSubRow.assignees) && !newSubRow.assignees.some(id => Number(id) === xId)) {
                    newSubRow.assignees.push(xId);
                    console.log(`[TeamPanel][HR-02] Added ${xId} to flat subordinates of newParent ${newParentId}`);
                }
            }

            // 2. If newParentId === oldParentId, reporting structure didn't change (only other fields),
            // but we can trigger a sync just in case details were updated.
            if (newParentId === oldParentId) {
                console.log(`[TeamPanel][HR-03] Parent UNCHANGED (${newParentId}) — no tree move needed, syncing UI only`);
                this.syncTeamPanelDataForContext();
                this.setSelectedMemberTasks();
                console.log("[TeamPanel] ━━━ HIERARCHY SYNC END (no change) ━━━");
                return;
            }

            // Determine transition type for logging (currentUserProfileId declared above)
            const isBecomingSubordinate = Number(newParentId) === Number(currentUserProfileId);
            const wasSubordinate = Number(oldParentId) === Number(currentUserProfileId);
            if (isBecomingSubordinate && !wasSubordinate) {
                console.log(`[TeamPanel][TR-01] Ad-hoc → Subordinate transition: ${xId} now reports to current user ${currentUserProfileId}`);
            } else if (!isBecomingSubordinate && wasSubordinate) {
                console.log(`[TeamPanel][TR-02] Subordinate → Ad-hoc transition: ${xId} no longer reports to current user ${currentUserProfileId}, now reports to ${newParentId}`);
            } else {
                console.log(`[TeamPanel][HR-04] Hierarchy change: ${xId} moved from parent ${oldParentId} → ${newParentId}`);
            }

            // 3. Identify affected tree context IDs
            const affectedTreeIds = new Set();

            // Always re-fetch the logged-in user's own tree (root context)
            const loggedInUserId = Number(useUserStore().profile.id);
            if (this.team_panel_data_map.has(loggedInUserId)) {
                affectedTreeIds.add(loggedInUserId);
            }

            // Also re-fetch the currently active UI context if drilled-down
            const activeContextId = Number(this.getCurrentUserProfileId());
            if (this.team_panel_data_map.has(activeContextId)) {
                affectedTreeIds.add(activeContextId);
            }

            // Re-fetch old parent's tree (the tree that lost xId)
            if (oldParentId && this.team_panel_data_map.has(oldParentId)) {
                affectedTreeIds.add(oldParentId);
            }

            // Re-fetch new parent's tree (the tree that gained xId)
            if (newParentId && this.team_panel_data_map.has(newParentId)) {
                affectedTreeIds.add(newParentId);
            }

            // Re-fetch any tree whose root is in the upline chain
            for (const upId of uplineSet) {
                if (this.team_panel_data_map.has(upId)) {
                    affectedTreeIds.add(upId);
                }
            }

            console.log("[TeamPanel][HR-REFETCH] Affected trees to re-fetch:", [...affectedTreeIds]);

            // 4. Re-fetch each affected tree in parallel
            const fetchPromises = [...affectedTreeIds].map(async (treeCtxId) => {
                try {
                    console.log(`[TeamPanel][HR-REFETCH] Fetching GET /team-panel/${treeCtxId}/${treeCtxId}`);
                    const response = await axios.get(`/team-panel/${treeCtxId}/${treeCtxId}`);
                    this.clearForbiddenProfile(treeCtxId);
                    const freshData = response.data?.data ?? {};
                    this.team_panel_data_map.set(treeCtxId, freshData);
                    console.log(`[TeamPanel][HR-REFETCH] Success: tree=${treeCtxId} replaced with fresh data`);
                } catch (err) {
                    if (err.response?.status === 403) {
                        console.log(`[TeamPanel][HR-REFETCH] 403 for tree=${treeCtxId} — markProfileAsForbidden`);
                        this.markProfileAsForbidden(treeCtxId);
                    }
                    console.error(`[TeamPanel][HR-REFETCH] Failed to re-fetch tree=${treeCtxId}`, err);
                }
            });

            await Promise.all(fetchPromises);
            
            // Clear any stale forbidden flag for the moved user
            this.clearForbiddenProfile(xId);

            // Trigger reactivity + UI refresh
            this.team_panel_data_map = new Map(this.team_panel_data_map);
            this.syncTeamPanelDataForContext();
            this.setSelectedMemberTasks();
            useAppState().requestTeamDashboardRefresh();
            console.log("[TeamPanel] ━━━ HIERARCHY SYNC END ━━━");
        },
        async sortSubordinatesOfParent(parentProfileId, treeContextId) {
            const tree = this.team_panel_data_map.get(Number(treeContextId));
            if (!tree) return;

            const rootData = tree.data ?? tree;
            const parentNode = this.findProfileNodeInTree(rootData, parentProfileId);
            if (!parentNode) return;

            if (!Array.isArray(parentNode.subordinates) || parentNode.subordinates.length <= 1) {
                return;
            }

            const profileIds = parentNode.subordinates.map(sub => Number(sub.user_profile_id ?? sub.id));

            try {
                const response = await axios.post(`/team-panel/sort/${parentProfileId}`, {
                    profile_ids: profileIds
                });

                const sortedIds = response.data?.data?.sorted_ids ?? [];
                if (Array.isArray(sortedIds) && sortedIds.length > 0) {
                    const idMap = new Map(parentNode.subordinates.map(sub => [Number(sub.user_profile_id ?? sub.id), sub]));
                    const sortedSubordinates = [];
                    sortedIds.forEach(id => {
                        const subNode = idMap.get(Number(id));
                        if (subNode) {
                            sortedSubordinates.push(subNode);
                        }
                    });

                    parentNode.subordinates.forEach(sub => {
                        const subId = Number(sub.user_profile_id ?? sub.id);
                        if (!sortedIds.includes(subId)) {
                            sortedSubordinates.push(sub);
                        }
                    });

                    parentNode.subordinates = sortedSubordinates;

                    this.team_panel_data_map = new Map(this.team_panel_data_map);
                    this.syncTeamPanelDataForContext();
                    this.setSelectedMemberTasks();
                }
            } catch (error) {
                console.error("[TeamPanelStore] failed to sort subordinates of parent", error);
            }
        },
    },
});
